import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Upload, X, CheckCircle2, AlertCircle, Loader2,
  ArrowLeft, Download, Cloud, Tag, Check, RefreshCw, Plus, GripVertical, TriangleAlert,
  Sparkles, Users, Pencil,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { toast } from "@/lib/toast";
import type { WizardState, ProcessedFile } from "@/pages/UploadWizard";
import VideoThumbnail from "@/components/VideoThumbnail";
import { ProviderPicker } from "@/components/ProviderPicker";
import { useUploadContext } from "@/contexts/UploadContext";
import {
  getFileCreationDate,
  formatDateFolder as formatDateFolderFromMetadata,
  type DateSource,
} from "@/lib/fileCreationDate";
import {
  activitiesTriggerTranscription,
  AUDIO_ACCEPT_FRAGMENT,
  isAudioFile,
} from "@shared/const";
import { extractAudioForTranscription, prewarmFFmpeg, MAX_CLIENT_EXTRACTION_BYTES } from "@/lib/audioExtractor";
import { USE_DIRECT_DRIVE_UPLOAD } from "@/const";
import { assignUniqueNames, buildRenamedName, replaceActivities, withCopyNumber } from "@shared/clipName";

interface FootageStepProps {
  wizardState: WizardState;
  /**
   * Called after the upload pipeline finishes (or after the user dismisses
   * the proceed-anyway dialog). `resolvedTopic` is the final Video Topic
   * after the post-upload AI-suggestion flow — undefined in the patient
   * workflow, and in the provider workflow it equals either the topic the
   * user typed up front or the suggestion they accepted afterwards.
   */
  onComplete: (
    files: ProcessedFile[],
    resolvedTopic?: string,
  ) => void | Promise<void>;
  onBack: () => void;
  /**
   * "patient" (default) tags clips with Activity Tags; "provider" tags clips
   * with Topic Tags and recolors the label editor to match the Topic Tag
   * Library page (Minnesota purple).
   */
  subjectKind?: "patient" | "provider";
}

type FilePhase = "queued" | "tagging" | "transcribing" | "uploading" | "uploading_final" | "done" | "error" | "skipped";

interface FileEntry {
  file: File;
  id?: number;
  /** Drive file ID returned by /api/drive-upload/chunk once the upload finishes */
  driveFileId?: string;
  phase: FilePhase;
  originalName: string;
  /** Activity tags for this clip */
  activities: string[];
  /** True when the operator explicitly pressed "Skip Tag" on this clip. The
   *  clip is still uploaded with the untagged naming convention, but is treated
   *  as intentionally blank — it does NOT count toward the "X untagged clips"
   *  warning at finalize. Adding a tag clears this flag automatically. */
  tagSkipped?: boolean;
  /** Per-clip provider name(s) added on the upload screen (patient flow).
   *  ADDITIVE to the session-level batch providers — the finalize write
   *  sends the union of both. Empty/undefined for the common case where the
   *  clip just inherits the batch providers. */
  providers?: string[];
  /** Live-computed from activities */
  renamedName: string;
  error?: string;
  /** Human-readable error reason from backend */
  errorReason?: string;
  /** Machine-readable error code from backend */
  errorCode?: string;
  progress: number;
  /** Upload start timestamp (ms) for ETA calculation */
  uploadStartTime?: number;
  /** Bytes uploaded so far */
  uploadBytesLoaded?: number;
  /** Total bytes to upload */
  uploadTotalBytes?: number;
  /** First-frame thumbnail as a data URL (generated client-side) */
  thumbnail?: string;
  /** Natural video dimensions for aspect-ratio-correct display */
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  /** Google Drive upload status for this file */
  drivePhase?: "idle" | "uploading" | "done" | "error";
  /** Rolling speed samples: array of [timestamp, bytesLoaded] for speed calculation */
  speedSamples?: Array<[number, number]>;
  /** Current transfer speed in bytes/sec (rolling average) */
  speedBps?: number;
  /** Estimated seconds remaining */
  etaSec?: number;
  /** Drive upload progress 0-100 */
  driveProgress?: number;
  /** Drive upload bytes transferred */
  driveBytesLoaded?: number;
  /** Drive upload total bytes */
  driveTotalBytes?: number;
  /** Drive upload speed in bytes/sec */
  driveSpeedBps?: number;
  /** Drive upload ETA in seconds */
  driveEtaSec?: number;
  /** Drive upload start timestamp (ms) for speed calculation */
  driveUploadStartTime?: number;
  /** Rolling speed samples for Drive upload */
  driveSpeedSamples?: Array<[number, number]>;
  /** MM.DD.YY derived from this file's embedded creation date (or
   *  lastModified fallback). Populated asynchronously after the file is
   *  added — undefined while detection is still running. */
  detectedDate?: string;
  /** Where the detected date came from. "lastModified" is a low-confidence
   *  fallback and is surfaced in the UI as a warning. */
  detectedDateSource?: DateSource;
  /** User-overridden date for this file. When set, takes precedence over
   *  detectedDate when routing to the MM.DD.YY folder. */
  overrideDate?: string;
  /** Drive folder ID this file was routed to (set by finalizeAll once the
   *  per-group MM.DD.YY/ folder has been created). Used by retryDriveUpload. */
  resolvedDriveFolderId?: string;
  /** Plain transcript text returned by Bedrock, captured at step 3.5 so the
   *  post-upload Topic Tag review dialog can feed it back through Claude
   *  for clips the user didn't tag manually. Provider A-Roll only. */
  transcript?: string;
  /** Sub-phase while `phase === "transcribing"`:
   *    "extracting"  → client-side ffmpeg.wasm audio extraction (0-100% in progress)
   *    "uploading"   → POSTing the extracted audio to /api/provider-transcribe
   *    "transcribing"→ server has the audio, Whisper + Claude are working
   *  Lets the file card swap labels without polluting the existing
   *  `progress` field's semantics. */
  transcribePhase?: "extracting" | "uploading" | "transcribing";
  /** Status of the post-upload server-side Pegasus + Whisper + embed job.
   *    "pending" → still running (incl. proxy transcode for large sources)
   *    "done"    → ai_description + embedding saved to footage_files
   *    "failed"  → server returned an error; the row still has the file
   *                  but no AI sidecar data
   *  Only meaningful once `phase === "done"`. Driven by the Pegasus poll
   *  in UploadContext via the onStatusChange callback. */
  pegasusPhase?: "pending" | "done" | "failed";
  /** MM.DD.YY[ - exam] folder name this file was actually saved under.
   *  Populated by finalizeAll for display in the confirmation step. */
  resolvedDateFolder?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Compute the trailing " - {something}" suffix to append to the MM.DD.YY date
 * folder. Provider A-Roll's free-text videoTopic wins over the patient-flow
 * isExam toggle — in practice the two are mutually exclusive (the topic input
 * is hidden on patient pages, exam toggle is hidden on provider pages). Topic
 * is sanitized of path-illegal characters (Drive + macOS + Windows safe).
 */
function buildDateFolderSuffix(wizardState: WizardState): string {
  const rawTopic = wizardState.videoTopic?.trim() ?? "";
  if (rawTopic) {
    // Strip characters illegal in folder names on macOS/Windows/Drive.
    // Mirrors the patientSuffix sanitizer in PatientStep.tsx.
    const sanitized = rawTopic.replace(/[<>:"/\\|?*]/g, "_").trim();
    if (sanitized) return ` - ${sanitized}`;
  }
  return wizardState.isExam ? " - exam" : "";
}

/** Format bytes/sec into a human-readable speed string */
function formatSpeed(bps: number): string {
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bps >= 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
  return `${Math.round(bps)} B/s`;
}

/** Format seconds into a human-readable ETA string */
function formatEta(sec: number): string {
  if (sec < 5) return "almost done";
  if (sec < 60) return `${Math.round(sec)}s left`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s left` : `${m}m left`;
}

/** Above this size, hash in pieces instead of loading the whole file. */
const STREAM_HASH_THRESHOLD_BYTES = 256 * 1024 * 1024;
const STREAM_HASH_CHUNK_BYTES = 8 * 1024 * 1024;

/**
 * SHA-256 of a File's bytes, returned as a 64-char lowercase hex string.
 * The "one clip, one row" dedup key — matches the format the NAS scanner
 * sends. Small files use native SubtleCrypto (one shot, hardware-
 * accelerated). Large files (camera masters can be many GB) are hashed in
 * 8 MB pieces with hash-wasm's incremental SHA-256, because SubtleCrypto
 * needs the whole file in one ArrayBuffer and browsers can't allocate one
 * past ~2 GB — those files used to get no hash and so a duplicate NAS row.
 *
 * Returns null on failure (e.g. a permission failure reading the file).
 * Callers should treat null as "no hash available" and skip the
 * contentHash field — the server falls back to creating a new row, so
 * nothing breaks; the nightly dedup merge collapses any resulting dupes.
 */
async function sha256HexOfFile(file: File | Blob): Promise<string | null> {
  try {
    if (file.size > STREAM_HASH_THRESHOLD_BYTES) {
      const { createSHA256 } = await import("hash-wasm");
      const hasher = await createSHA256();
      hasher.init();
      for (let offset = 0; offset < file.size; offset += STREAM_HASH_CHUNK_BYTES) {
        const chunk = await file.slice(offset, offset + STREAM_HASH_CHUNK_BYTES).arrayBuffer();
        hasher.update(new Uint8Array(chunk));
      }
      return hasher.digest("hex");
    }
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch (err) {
    console.warn("[hash] sha256 failed — proceeding without contentHash:", err);
    return null;
  }
}

const PHASE_LABELS: Record<FilePhase, string> = {
  queued: "Queued",
  tagging: "Add Tags",
  transcribing: "Transcribing...",
  uploading: "Uploading...",
  uploading_final: "Uploading...",
  done: "Complete",
  error: "Failed",
  skipped: "Skipped",
};

const PHASE_COLORS: Record<FilePhase, string> = {
  queued: "bg-muted text-muted-foreground",
  tagging: "bg-amber-100 text-amber-700",
  transcribing: "bg-violet-100 text-violet-700",
  uploading: "bg-green-100 text-green-700",
  uploading_final: "bg-cyan-100 text-cyan-700",
  done: "bg-emerald-100 text-emerald-700",
  error: "bg-red-100 text-red-700",
  skipped: "bg-orange-100 text-orange-700",
};

// ─── Sortable tag chip (used inside LabelEditor) ─────────────────────────────

interface SortableTagChipProps {
  id: string;
  activity: string;
  index: number;
  disabled?: boolean;
  isEditing: boolean;
  editingValue: string;
  onStartEdit: (index: number) => void;
  onCommitEdit: () => void;
  onEditKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEditValueChange: (v: string) => void;
  onRemove: (index: number) => void;
}

function SortableTagChip({
  id, activity, index, disabled, isEditing,
  editingValue, onStartEdit, onCommitEdit, onEditKeyDown, onEditValueChange, onRemove,
}: SortableTagChipProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  if (isEditing) {
    return (
      <span ref={setNodeRef} style={style} className="inline-flex items-center gap-1">
        <input
          autoFocus
          value={editingValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onKeyDown={onEditKeyDown}
          onBlur={onCommitEdit}
          className="text-xs px-2 py-0.5 rounded-full border border-primary bg-background text-foreground outline-none w-32"
        />
      </span>
    );
  }

  return (
    <span
      ref={setNodeRef}
      style={style}
      className={cn(
        "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium transition-all select-none",
        // Light mode is untouched. In dark mode the chip flips to a near-white
        // background (subtle cool tint via --foreground) so the primary-colored
        // text and outline pop the way the suggested-tag chips do. Outline
        // (not border) so layout/sizing is identical in both modes.
        "dark:outline dark:outline-1 dark:-outline-offset-1 dark:outline-primary/60",
        disabled
          ? "bg-primary/10 dark:bg-foreground/95 text-primary"
          : "bg-primary/10 dark:bg-foreground/95 text-primary hover:bg-primary/20 dark:hover:bg-foreground",
        isDragging && "ring-2 ring-primary/40 shadow-md",
      )}
    >
      {/* Drag handle — only shown when not disabled */}
      {!disabled && (
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-primary/40 hover:text-primary/70 -ml-0.5 mr-0.5 touch-none"
          title="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3 h-3" />
        </span>
      )}
      <span
        className={disabled ? undefined : "cursor-pointer"}
        title={disabled ? undefined : "Click to edit"}
        onClick={() => !disabled && onStartEdit(index)}
      >
        {activity}
      </span>
      {!disabled && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          className="ml-0.5 rounded-full text-primary/60 hover:text-primary"
          title="Remove label"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

// ─── Label Editor sub-component ───────────────────────────────────────────────

interface LabelEditorProps {
  activities: string[];
  /** Tags that were AI-suggested (subset of activities) — rendered with purple tint + AI badge */

  onChange: (updated: string[]) => void;
  disabled?: boolean;
  /** Tags already used on other clips this session — shown as one-click suggestions */
  sessionSuggestions?: string[];
  /** "patient" → Activity Tags (default), "provider" → Topic Tags */
  subjectKind?: "patient" | "provider";
}

function LabelEditor({ activities, onChange, disabled, sessionSuggestions = [], subjectKind = "patient" }: LabelEditorProps) {
  const isProvider = subjectKind === "provider";
  const [inputValue, setInputValue] = useState("");
  // Index of the chip currently being edited inline (-1 = none)
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [editingValue, setEditingValue] = useState("");
  // dnd-kit sensors — use PointerSensor with a small activation distance so clicks still work
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = activities.indexOf(active.id as string);
    const newIndex = activities.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      onChange(arrayMove(activities, oldIndex, newIndex));
    }
  };
  // Autocomplete
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Pick the tag source: Activity Tags for patients, Topic Tags for providers.
  const tagsApi = isProvider ? trpc.topicTags : trpc.tags;

  // Load the whole tag library + alias list once and filter locally, so
  // matches appear on every keystroke instead of waiting on server round-trips
  // (which blanked the dropdown until a retype hit the cache). Mirrors the
  // server search: tags = substring match, most-used first, top 8; aliases =
  // prefix match, alphabetical, top 5.
  const queryOpts = { staleTime: 60_000, refetchOnWindowFocus: true };
  const { data: tagLibrary = [] } = tagsApi.list.useQuery(undefined, queryOpts);
  const { data: aliasLibrary = [] } = tagsApi.listAliases.useQuery(undefined, queryOpts);
  const query = inputValue.trim().toLowerCase();

  const suggestions = useMemo(() => {
    if (!query) return [];
    return tagLibrary
      .filter((t) => t.name.toLowerCase().includes(query))
      .sort((a, b) => b.usageCount - a.usageCount || a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [tagLibrary, query]);
  const filteredSuggestions = suggestions.filter((s) => !activities.includes(s.name));

  // Alias lookup — search aliases matching the current input prefix. An
  // alias may expand into multiple target tags; we treat `targets` as the
  // source of truth and fall back to `[canonicalName]` for pre-migration rows.
  const aliasMatches = useMemo(() => {
    if (!query) return [];
    return aliasLibrary.filter((a) => a.alias.toLowerCase().startsWith(query)).slice(0, 5);
  }, [aliasLibrary, query]);
  const aliasTargets = (a: { targets?: string[] | null; canonicalName: string }) =>
    (a.targets && a.targets.length > 0 ? a.targets : [a.canonicalName]).filter(Boolean);
  // Hide an alias suggestion only when *every* one of its targets is already on
  // the file (otherwise picking it would still add at least one missing tag).
  const filteredAliases = aliasMatches.filter((a) =>
    aliasTargets(a).some((t) => !activities.includes(t)),
  );

  // Resolve an alias if the exact input matches one, otherwise return the raw value
  const resolvedData = aliasLibrary;
  const exactAlias = resolvedData?.find((a) => a.alias.toLowerCase() === inputValue.trim().toLowerCase());

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addLabel = (value?: string) => {
    const raw = (value ?? inputValue).trim();
    if (!raw) return;
    // Resolve alias: if the typed value exactly matches a known alias, expand
    // into all of its targets. Multi-target aliases pull several tags onto the
    // file in one shot (e.g. "exam" → ATNR Check + STNR Check + …).
    const aliasMatch = (resolvedData ?? []).find((a) => a.alias.toLowerCase() === raw.toLowerCase());
    const expanded = aliasMatch ? aliasTargets(aliasMatch) : [raw];

    // Filter out tags already on this file (case-insensitive).
    const existingLower = new Set(activities.map((a) => a.toLowerCase()));
    const toAdd: string[] = [];
    for (const tag of expanded) {
      const trimmed = tag.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (existingLower.has(key)) continue;
      existingLower.add(key);
      toAdd.push(trimmed);
    }

    if (toAdd.length === 0) {
      toast.error(aliasMatch ? "All targets are already on this clip" : "That label already exists");
      return;
    }

    onChange([...activities, ...toAdd]);
    setInputValue("");
    setShowSuggestions(false);
    setHighlightedIdx(-1);
  };
  const removeLabel = (index: number) => {
    onChange(activities.filter((_, i) => i !== index));
  };
  const startEdit = (index: number) => {
    if (disabled) return;
    setEditingIndex(index);
    setEditingValue(activities[index]);
  };
  const commitEdit = () => {
    const trimmed = editingValue.trim();
    if (trimmed && trimmed !== activities[editingIndex]) {
      const updated = activities.map((a, i) => (i === editingIndex ? trimmed : a));
      onChange(updated);
    }
    setEditingIndex(-1);
    setEditingValue("");
  };
  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") { setEditingIndex(-1); setEditingValue(""); }
  };
  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const totalItems = filteredSuggestions.length + filteredAliases.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, totalItems - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIdx >= 0 && highlightedIdx < filteredSuggestions.length) {
        // Highlighted item is a regular tag row (tags come first now)
        if (filteredSuggestions[highlightedIdx]) addLabel(filteredSuggestions[highlightedIdx].name);
      } else if (highlightedIdx >= filteredSuggestions.length) {
        // Highlighted item is an alias row (aliases come last)
        const aliasIdx = highlightedIdx - filteredSuggestions.length;
        if (filteredAliases[aliasIdx]) addLabel(filteredAliases[aliasIdx].alias);
      } else {
        addLabel();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    // data-preview-keep-open: clicks inside any tag UI (chips, dropdown,
    // suggestion chips, add input) must NOT dismiss an open VideoThumbnail
    // preview popover. The popover's outside-click handler checks for this
    // attribute on the click target's ancestors. Explicit empty-string value
    // ensures the attribute renders consistently across React builds.
    <div data-preview-keep-open="" className={cn("space-y-2 mt-2", isProvider && "theme-topic-tags")}>
      {/* Existing chips — drag-and-drop sortable */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={activities} strategy={horizontalListSortingStrategy}>
          <div className="flex flex-wrap gap-1.5">
            {activities.map((activity, i) => (
              <SortableTagChip
                key={activity}
                id={activity}
                activity={activity}
                index={i}
                disabled={disabled}
                isEditing={editingIndex === i}
                editingValue={editingValue}
                onStartEdit={startEdit}
                onCommitEdit={commitEdit}
                onEditKeyDown={handleEditKeyDown}
                onEditValueChange={setEditingValue}
                onRemove={removeLabel}
              />
            ))}
            {activities.length === 0 && (
              <span className="text-xs text-muted-foreground italic">No labels yet — add one below</span>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Session suggestions */}
      {!disabled && sessionSuggestions.filter((s) => !activities.includes(s)).length > 0 && (
        <div data-preview-keep-open="" className="mt-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Suggested from this session</p>
          <div className="flex flex-wrap gap-1.5">
            {sessionSuggestions
              .filter((s) => !activities.includes(s))
              .map((tag) => (
                <button
                  key={tag}
                  type="button"
                  data-preview-keep-open=""
                  onClick={() => addLabel(tag)}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border border-blue-400/40 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                >
                  + {tag}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Add new label input */}
      {!disabled && (
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowSuggestions(true);
                setHighlightedIdx(-1);
              }}
              onFocus={() => inputValue.trim() && setShowSuggestions(true)}
              onKeyDown={handleAddKeyDown}
              placeholder={isProvider ? "Add topic label..." : "Add activity label..."}
              className="h-8 text-xs"
              autoComplete="off"
            />
            {/* Autocomplete dropdown */}
            {showSuggestions && (filteredSuggestions.length > 0 || filteredAliases.length > 0) && (
              <div
                ref={dropdownRef}
                data-preview-keep-open=""
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover text-popover-foreground border border-border rounded-lg shadow-md overflow-hidden"
              >
                {/* Regular tag matches — shown first, sorted alphabetically by the server */}
                {filteredSuggestions.map((tag, idx) => (
                  <button
                    key={tag.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); addLabel(tag.name); }}
                    className={cn(
                      "w-full text-left px-3 py-2 text-xs flex items-start justify-between gap-2 hover:bg-primary/10 hover:text-primary transition-colors",
                      idx === highlightedIdx && "bg-primary/15 text-primary font-semibold"
                    )}
                  >
                    <span className="flex flex-col gap-0.5 min-w-0">
                      <span className="font-medium text-foreground">{tag.name}</span>
                      {tag.description && (
                        <span className="text-muted-foreground text-[10px] truncate">{tag.description}</span>
                      )}
                    </span>
                    <span className="text-muted-foreground text-[10px] shrink-0 mt-0.5">{tag.usageCount}x</span>
                  </button>
                ))}
                {/* Alias matches — shown last with a special badge */}
                {filteredAliases.map((alias, aliasIdx) => {
                  const targets = aliasTargets(alias);
                  return (
                    <button
                      key={`alias-${alias.id}`}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); addLabel(alias.alias); }}
                      className={cn(
                        "w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-primary/10 hover:text-primary transition-colors border-t border-border/50",
                        (aliasIdx + filteredSuggestions.length) === highlightedIdx && "bg-primary/15 text-primary font-semibold"
                      )}
                    >
                      <span className="text-[9px] font-bold bg-amber-400 text-white rounded px-1 py-0 leading-4 shrink-0">ALIAS</span>
                      <span className="font-mono font-semibold">{alias.alias}</span>
                      <span className="opacity-60">→</span>
                      <span className="font-medium flex-1 truncate">{targets.join(" + ")}</span>
                      {targets.length > 1 && (
                        <span className="text-[9px] font-bold bg-primary/15 text-primary rounded px-1 py-0 leading-4 shrink-0">×{targets.length}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Inline alias hint below the input when exact alias is matched */}
            {exactAlias && !showSuggestions && (() => {
              const targets = aliasTargets(exactAlias);
              return (
                <p className="mt-1 text-[10px] text-amber-600 flex items-center gap-1 flex-wrap">
                  <span className="font-semibold">{exactAlias.alias}</span>
                  <span>→</span>
                  <span className="truncate">{targets.join(" + ")}</span>
                  <span className="text-muted-foreground">
                    (press Enter to add {targets.length > 1 ? `${targets.length} tags` : ""})
                  </span>
                </p>
              );
            })()}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => addLabel()}
            disabled={!inputValue.trim()}
            className="h-8 px-3 text-xs flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Per-clip provider add-on ─────────────────────────────────────────────────
// Subtle, collapsed-by-default control shown under a clip's Activity Tags
// (patient flow). Normally just a small "+ Add provider to this clip" link so
// the field doesn't clutter the card. Clicking reveals the shared
// ProviderPicker; whatever is chosen here is ADDITIVE to the session's batch
// providers (the finalize write sends the union).

function ClipProviderAddon({
  value,
  onChange,
  sessionSuggestions = [],
}: {
  value: string[];
  onChange: (next: string[]) => void;
  /** One-off providers already used on OTHER clips this session — offered as
   *  quick-add chips once the section is revealed (mirrors the activity-tag
   *  "Suggested from this session" row). */
  sessionSuggestions?: string[];
}) {
  const [open, setOpen] = useState(value.length > 0);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border border-border bg-muted/60 text-foreground/80 hover:bg-muted transition-colors"
      >
        <Plus className="w-3 h-3" />
        Add provider to this clip
      </button>
    );
  }

  const addSuggestion = (name: string) => {
    if (!value.some((v) => v.toLowerCase() === name.toLowerCase())) {
      onChange([...value, name]);
    }
  };
  const unusedSuggestions = sessionSuggestions.filter(
    (s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()),
  );

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Provider — this clip only
        </span>
      </div>
      <ProviderPicker
        value={value}
        onChange={onChange}
        compact
        autoFocus
        placeholder="Add a provider to just this clip…"
      />
      {/* One-off providers used elsewhere this session — quick re-add. */}
      {unusedSuggestions.length > 0 && (
        <div>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
            Used on other clips this session
          </p>
          <div className="flex flex-wrap gap-1.5">
            {unusedSuggestions.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => addSuggestion(name)}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border border-blue-400/40 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              >
                + {name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Video first-frame thumbnail extractor ───────────────────────────────────

type ThumbnailResult = { dataUrl: string; width: number; height: number } | undefined;

function extractFirstFrame(file: File): Promise<ThumbnailResult> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    const cleanup = () => URL.revokeObjectURL(url);
    video.addEventListener("loadeddata", () => {
      video.currentTime = 0.1; // seek slightly past 0 to avoid black frame
    }, { once: true });
    video.addEventListener("seeked", () => {
      try {
        const vw = video.videoWidth || 160;
        const vh = video.videoHeight || 90;
        const canvas = document.createElement("canvas");
        // Render at native ratio, capped at 320px wide for reasonable data URL size
        const scale = Math.min(1, 320 / vw);
        canvas.width = Math.round(vw * scale);
        canvas.height = Math.round(vh * scale);
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.7), width: vw, height: vh });
        } else {
          resolve(undefined);
        }
      } catch {
        resolve(undefined);
      } finally {
        cleanup();
      }
    }, { once: true });
    video.addEventListener("error", () => { cleanup(); resolve(undefined); }, { once: true });
    // Fallback: if seeked never fires within 3s, give up
    setTimeout(() => { cleanup(); resolve(undefined); }, 3000);
  });
}

// ─── Image thumbnail extractor ─────────────────────────────────────────────────

function isImageFile(file: File): boolean {
  // Covers HEIC/HEIF which browsers may report as empty or non-standard MIME types
  const mime = file.type.toLowerCase();
  if (mime.startsWith("image/")) return true;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ["heic", "heif", "jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "tif", "avif"].includes(ext);
}

function extractImageThumbnail(file: File): Promise<ThumbnailResult> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const iw = img.naturalWidth || 160;
        const ih = img.naturalHeight || 90;
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 320 / iw);
        canvas.width = Math.round(iw * scale);
        canvas.height = Math.round(ih * scale);
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.85), width: iw, height: ih });
        } else {
          resolve(undefined);
        }
      } catch {
        resolve(undefined);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(undefined); };
    img.src = url;
    setTimeout(() => { URL.revokeObjectURL(url); resolve(undefined); }, 5000);
  });
}

// ─── Universal download helper (works in Chrome, Firefox, Safari, Edge) ─────────

function triggerDownload(file: File, filename: string) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Must be in DOM for Firefox to fire the download
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Small delay before cleanup so Firefox has time to initiate the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

// ─── Bedrock transcription helpers ───────────────────────────────────────────

/**
 * Transcription is expensive — ffmpeg audio extract + Bedrock invoke per clip.
 * The upload loop runs 3 files in parallel, and a batch with several clips
 * tagged "Explanation"/"Discussion" would otherwise fire several transcribe
 * POSTs simultaneously. That spikes server memory enough to OOM the Node
 * process and 502 the proxy. Funnel every transcribe POST through this chain
 * so at most one is in flight at a time across the whole client — other
 * upload work (Drive, finalize, sidecar writes) still runs in parallel.
 */
let transcribeQueue: Promise<unknown> = Promise.resolve();

function runTranscribeSerially<T>(task: () => Promise<T>): Promise<T> {
  const next = transcribeQueue.then(task, task);
  // Don't poison the chain on failure — the next caller should still proceed.
  transcribeQueue = next.catch(() => undefined);
  return next;
}

/**
 * Upload an audio Blob in 5 MB chunks to /api/provider-transcribe-chunk.
 * Each chunk POSTs the raw binary slice with `X-Chunk-Offset` so the
 * server can validate ordering and let the client retry a single failed
 * chunk without re-sending the whole upload.
 *
 * Why chunked: a single multipart upload of a 100+ MB audio file on a
 * residential upload speed can easily outlast a reverse-proxy read timeout
 * (Coolify/Traefik default 60 s). Splitting it into ~5 MB pieces means no
 * individual request is in flight long enough to trip that timeout.
 *
 * Returns the sessionId the server will look up when /api/provider-transcribe
 * is called with `?sessionId=`.
 */
async function uploadAudioChunked(
  audio: Blob,
  filename: string,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const sessionId = crypto.randomUUID();
  // 2 MB chunks — small enough to comfortably finish within any
  // reasonable proxy timeout window even on slow uplinks, large enough
  // to keep request overhead modest. 100 MB audio = 50 chunks.
  const chunkSize = 2 * 1024 * 1024;
  const totalBytes = audio.size;
  let offset = 0;
  while (offset < totalBytes) {
    const end = Math.min(offset + chunkSize, totalBytes);
    const chunk = audio.slice(offset, end);
    const buffer = await chunk.arrayBuffer();

    // Retry each chunk up to 3 times before giving up. Transient proxy
    // hiccups or wifi blips shouldn't kill the whole upload.
    //
    // 409 resync: if the proxy bailed on the response AFTER the server
    // wrote the chunk to disk, our retry would re-send the same offset
    // and get a 409 with the server's actual `receivedBytes`. Jump
    // ahead to that offset instead of looping forever.
    let lastErr: Error | null = null;
    let serverReceivedBytes: number | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch("/api/provider-transcribe-chunk", {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            "X-Transcribe-Session": sessionId,
            "X-Chunk-Offset": String(offset),
            "X-Chunk-Total": String(totalBytes),
            "X-Chunk-Filename": filename,
          },
          body: buffer,
        });
        if (res.status === 409) {
          // Server has more bytes than we sent — our previous chunk
          // landed but the response didn't reach us. Resync and stop
          // retrying THIS chunk; the outer loop will pick up from the
          // server's offset.
          const body = await res.json().catch(() => ({}));
          if (typeof body.receivedBytes === "number") {
            console.warn(
              `[uploadAudioChunked] 409 resync: server has ${body.receivedBytes} bytes, we sent offset ${offset} — jumping ahead.`,
            );
            serverReceivedBytes = body.receivedBytes;
            break;
          }
          // 409 without a usable receivedBytes — treat as a normal error.
          throw new Error(body.error ?? "Chunk offset mismatch");
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `Chunk upload failed (${res.status})`);
        }
        lastErr = null;
        break;
      } catch (err) {
        lastErr = err instanceof Error ? err : new Error(String(err));
        console.warn(
          `[uploadAudioChunked] chunk offset=${offset} attempt ${attempt + 1}/3 failed:`,
          lastErr.message,
        );
        // Backoff: 500ms, 1500ms between retries.
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 500 * Math.pow(3, attempt)));
        }
      }
    }

    if (serverReceivedBytes !== null) {
      if (serverReceivedBytes < offset) {
        throw new Error(
          `Upload state inconsistent: server reports ${serverReceivedBytes} bytes but we sent offset ${offset}`,
        );
      }
      offset = serverReceivedBytes;
    } else if (lastErr) {
      throw lastErr;
    } else {
      offset = end;
    }

    onProgress?.(Math.round((offset / totalBytes) * 100));
  }
  return sessionId;
}

/**
 * Three-phase transcription pipeline:
 *   1. Extract a Whisper-friendly mp3 client-side via ffmpeg.wasm — turns
 *      a 15 GB source video into ~100 MB of audio before anything hits
 *      the network.
 *   2. POST that audio in 5 MB chunks via /api/provider-transcribe-chunk
 *      so no single request lasts long enough to trip a reverse-proxy
 *      read timeout.
 *   3. POST /api/provider-transcribe?sessionId=X to kick off Bedrock on
 *      the assembled tmp file. The response uses a keep-alive heartbeat
 *      so the long Whisper+Claude work doesn't 502 either.
 *
 * Returns null when the server has Bedrock disabled (503) so the caller
 * can silently skip. Phase callbacks are split so the file card can show
 * "Extracting audio…" then "Uploading audio…" then
 * "Transcribing & summarizing…".
 */
async function transcribeProviderVideo(
  file: File,
  onPhaseChange?: (phase: "extracting" | "uploading" | "transcribing", pct: number) => void,
): Promise<{ txtContent: string; srtContent: string; transcript?: string } | null> {
  if (!file.type.startsWith("video/")) return null;

  // Per-phase wall-clock timing. Boundary stamps drive `[transcribe-timing]`
  // console lines so upload-screen slowdowns are easy to attribute
  // (ffmpeg vs network vs Bedrock) from the browser dev console.
  const tStart = performance.now();
  let tExtractStart = 0;
  let tUploadStart = 0;
  let tTranscribeStart = 0;
  const secsSince = (t: number) => ((performance.now() - t) / 1000).toFixed(1);
  const logPhase = (phase: string, sinceMark: number) =>
    console.info(`[transcribe-timing] ${file.name} | ${phase} ${secsSince(sinceMark)}s`);

  // ── Phase 1: client-side audio extraction ────────────────────────────────
  // If ffmpeg.wasm fails (bundle blocked, browser incompatibility, OOM on
  // an enormous file), fall back to uploading the whole video — the server
  // can still handle it the old way (slower, more network-fragile, single
  // multipart on the legacy endpoint). Surface a toast when we fall back
  // so the user knows they're on the failure-prone path — silent fallback
  // makes proxy 502s look like mysterious failures.
  let audioBlob: Blob | null = null;
  let payloadFilename: string;
  if (file.size >= MAX_CLIENT_EXTRACTION_BYTES) {
    // Known dead end: files this large reliably trip a wasm-level
    // "memory access out of bounds" trap inside ffmpeg.wasm (see
    // MAX_CLIENT_EXTRACTION_BYTES in audioExtractor.ts) rather than a
    // catchable error. Skip straight to the fallback instead of burning
    // time on an attempt that's going to fail anyway, and use a calm
    // "large file" message instead of a scary crash-looking one.
    console.info(
      `[transcribe] ${file.name} is ${(file.size / 1024 ** 3).toFixed(1)} GB — skipping client-side audio extraction (unreliable above ~2 GB) and uploading the whole video instead.`,
    );
    toast.info(
      `"${file.name}" is a large video — uploading it directly instead of extracting audio first. This will take longer than usual.`,
      { duration: 8000 },
    );
    audioBlob = null;
    payloadFilename = file.name;
  } else {
    try {
      onPhaseChange?.("extracting", 0);
      tExtractStart = performance.now();
      audioBlob = await extractAudioForTranscription(file, (pct) => {
        onPhaseChange?.("extracting", pct);
      });
      logPhase("extracting", tExtractStart);
      // Swap the original extension to .mp3 so the server filename + log
      // labels reflect the audio payload rather than e.g. "input.MOV".
      payloadFilename = file.name.replace(/\.[^.]+$/, "") + ".mp3";
    } catch (err: any) {
      logPhase("extracting (failed → fallback)", tExtractStart || tStart);
      const reason = err?.message ?? String(err);
      console.warn(
        "[transcribe] Client-side audio extraction failed — falling back to whole-video upload:",
        reason,
      );
      toast.warning(
        `Audio extraction failed for "${file.name}" — falling back to whole-video upload. This is slower and may fail on large files. (${reason})`,
        { duration: 10000 },
      );
      audioBlob = null;
      payloadFilename = file.name;
    }
  }

  // ── Phase 2: network upload ──────────────────────────────────────────────
  // Chunked path when ffmpeg.wasm succeeded; single multipart fallback
  // when it didn't. Sessionless multipart still has to clear the 60s read
  // timeout on its own, but for the fallback case the user already has a
  // small file (or we'd have used ffmpeg.wasm).
  onPhaseChange?.("uploading", 0);
  if (audioBlob) {
    tUploadStart = performance.now();
    let sessionId: string;
    try {
      sessionId = await uploadAudioChunked(audioBlob, payloadFilename, (pct) => {
        onPhaseChange?.("uploading", pct);
      });
    } catch (err) {
      logPhase("uploading (failed)", tUploadStart);
      throw err instanceof Error
        ? err
        : new Error("Network error while uploading audio for transcription");
    }
    logPhase("uploading", tUploadStart);
    onPhaseChange?.("transcribing", 0);
    tTranscribeStart = performance.now();

    // ── Kick off the background job ────────────────────────────────────
    // The finalize POST starts Bedrock asynchronously on the server and
    // returns within milliseconds — no proxy timeout possible. The actual
    // result comes via the status endpoint below.
    const kickoffRes = await fetch(
      `/api/provider-transcribe?sessionId=${encodeURIComponent(sessionId)}`,
      { method: "POST" },
    );
    if (kickoffRes.status === 503) return null;
    if (!kickoffRes.ok) {
      const body = await kickoffRes.json().catch(() => ({}));
      throw new Error(body.error ?? `Transcription kickoff failed (${kickoffRes.status})`);
    }
    const kickoffBody = (await kickoffRes.json()) as
      | { jobId: string; status: "transcribing" | "done" | "error" }
      | { error: string };
    if ("error" in kickoffBody) throw new Error(kickoffBody.error);

    // ── Poll for completion ────────────────────────────────────────────
    // 3 second interval — fast enough to feel responsive, slow enough
    // that a 5-minute Bedrock run is just ~100 lightweight GETs. Hard
    // cap at 30 minutes so a stuck job eventually surfaces an error.
    const pollIntervalMs = 3000;
    const maxPollDurationMs = 30 * 60 * 1000;
    const pollStartedAt = Date.now();
    while (true) {
      if (Date.now() - pollStartedAt > maxPollDurationMs) {
        throw new Error("Transcription timed out after 30 minutes");
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      let statusRes: Response;
      try {
        statusRes = await fetch(
          `/api/provider-transcribe-status?jobId=${encodeURIComponent(kickoffBody.jobId)}`,
        );
      } catch (err) {
        // Transient network error during polling — try again next tick.
        // 30-minute total ceiling above keeps us from looping forever.
        console.warn("[transcribe] Status poll network error, retrying:", err);
        continue;
      }
      if (!statusRes.ok) {
        const body = await statusRes.json().catch(() => ({}));
        throw new Error(body.error ?? `Transcription status failed (${statusRes.status})`);
      }
      const status = (await statusRes.json()) as
        | { status: "uploading" | "transcribing" }
        | { status: "done"; txtContent: string; srtContent: string; transcript?: string }
        | { status: "error"; error: string };
      if (status.status === "done") {
        logPhase("transcribing", tTranscribeStart);
        logPhase("TOTAL", tStart);
        return {
          txtContent: status.txtContent,
          srtContent: status.srtContent,
          transcript: status.transcript,
        };
      }
      if (status.status === "error") {
        logPhase("transcribing (failed)", tTranscribeStart);
        throw new Error(status.error);
      }
      // Otherwise still working — loop.
    }
  }

  // Legacy / fallback: single multipart upload of the whole video.
  const form = new FormData();
  form.append("file", file, payloadFilename);
  tUploadStart = performance.now();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/provider-transcribe");
    xhr.responseType = "text";

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const pct = Math.round((event.loaded / event.total) * 100);
      onPhaseChange?.("uploading", pct);
    };
    xhr.upload.onloadend = () => {
      logPhase("uploading (fallback)", tUploadStart);
      onPhaseChange?.("transcribing", 0);
      tTranscribeStart = performance.now();
    };
    xhr.onerror = () => reject(new Error("Network error while uploading audio for transcription"));
    xhr.onabort = () => reject(new Error("Transcription upload aborted"));
    xhr.onload = () => {
      logPhase("transcribing (fallback)", tTranscribeStart);
      logPhase("TOTAL (fallback)", tStart);
      if (xhr.status === 503) { resolve(null); return; }
      if (xhr.status < 200 || xhr.status >= 300) {
        let message = `Transcription failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText) as { error?: string };
          if (body.error) message = body.error;
        } catch { /* fall through */ }
        reject(new Error(message));
        return;
      }
      try {
        const parsed = JSON.parse(xhr.responseText) as
          | { txtContent: string; srtContent: string; transcript?: string }
          | { error: string };
        if ("error" in parsed) { reject(new Error(parsed.error)); return; }
        resolve(parsed);
      } catch (err) {
        reject(err instanceof Error ? err : new Error("Failed to parse transcription response"));
      }
    };
    xhr.send(form);
  });
}

/** Write a text sidecar to the local date-folder handle (Chrome/Edge File System Access API). */
async function writeSidecarLocally(
  dateFolderHandle: FileSystemDirectoryHandle | null,
  filename: string,
  content: string,
): Promise<boolean> {
  if (!dateFolderHandle) return false;
  try {
    const fileHandle = await dateFolderHandle.getFileHandle(filename, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns the "sidecars" subfolder handle under the given parent,
 * creating it if absent. Used to keep transcript + description
 * sidecars tucked alongside the video instead of cluttering the date
 * folder. Returns null when the parent is null or the FS access
 * fails (browser without FSA support, denied permission, etc.).
 * Historically named "Ds+Ts" — the rename to "sidecars" makes the
 * folder's purpose self-evident to non-technical editors.
 */
async function ensureLocalSidecarSubfolder(
  parentHandle: FileSystemDirectoryHandle | null,
): Promise<FileSystemDirectoryHandle | null> {
  if (!parentHandle) return null;
  try {
    return await parentHandle.getDirectoryHandle("sidecars", { create: true });
  } catch {
    return null;
  }
}

// Pegasus 1.2 eligibility limits — must stay in sync with the server-side
// constants in src/server/bedrock.ts. AWS docs:
// https://docs.aws.amazon.com/bedrock/latest/userguide/model-parameters-pegasus.html
const PEGASUS_MIN_DURATION_SEC = 4;
const PEGASUS_MAX_DURATION_SEC = 60 * 60;
const PEGASUS_MAX_BYTES = 2 * 1024 * 1024 * 1024;

/** Chunk size for local-only Pegasus uploads. Mirrors the transcribe path. */
const PEGASUS_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * Stream a local-only video to the server in 5 MB chunks, then kick off
 * Pegasus on the staged tmp file. Used when Google Drive isn't a
 * destination — the server has no Drive copy to download from, so the
 * client provides the bytes. Returns false on any failure (caller logs).
 */
async function uploadVideoForLocalPegasus(
  file: File,
  footageFileId: number,
): Promise<boolean> {
  const sessionId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  const totalBytes = file.size;
  let offset = 0;
  try {
    while (offset < totalBytes) {
      const end = Math.min(offset + PEGASUS_CHUNK_SIZE_BYTES, totalBytes);
      const slice = file.slice(offset, end);
      const buf = await slice.arrayBuffer();
      const res = await fetch("/api/finalize-analysis-chunk", {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "X-Pegasus-Session": sessionId,
          "X-Chunk-Offset": String(offset),
          "X-Chunk-Total": String(totalBytes),
          "X-Chunk-Filename": file.name,
        },
        body: buf,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        // On a 409 resync the server tells us the canonical offset; jump
        // to it and retry that chunk. Other errors abort.
        if (res.status === 409 && typeof body.receivedBytes === "number") {
          offset = body.receivedBytes;
          continue;
        }
        console.warn("[local-pegasus] chunk upload failed:", body.error ?? res.statusText);
        return false;
      }
      offset = end;
    }
    const kickoff = await fetch("/api/finalize-analysis-local", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, footageFileId }),
    });
    if (!kickoff.ok) {
      const body = await kickoff.json().catch(() => ({ error: kickoff.statusText }));
      console.warn("[local-pegasus] kickoff failed:", body.error ?? kickoff.statusText);
      return false;
    }
    return true;
  } catch (err: any) {
    console.warn("[local-pegasus] upload errored:", err?.message ?? err);
    return false;
  }
}

/**
 * Probe a video file's duration client-side by loading metadata into a
 * detached <video> element. Resolves to null when the browser can't decode
 * the container (e.g. AVI on Safari) — caller should treat that as "can't
 * tell" and not block on it.
 */
async function probeVideoDurationSeconds(file: File): Promise<number | null> {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return null;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    let settled = false;
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(value);
    };
    video.onloadedmetadata = () => {
      const d = video.duration;
      finish(Number.isFinite(d) && d > 0 ? d : null);
    };
    video.onerror = () => finish(null);
    // Safety timeout — slow files / weird codecs.
    window.setTimeout(() => finish(null), 5000);
    video.src = url;
  });
}

/**
 * Predict whether a given file would receive a Pegasus AI description.
 * Used to skip the fire-and-forget call when we know server-side checks
 * would reject the file, and to surface a single summary toast at the
 * end of the upload batch listing what was skipped and why.
 *
 * Mirrors the server-side preconditions in analyzeVideoWithPegasus. When
 * we can't probe duration (Safari + AVI, etc.), we err on the side of
 * letting it through — the server will reject and log if Pegasus balks.
 *
 * Drive is no longer required: when the user picks local-only, the
 * client streams the video bytes to /api/finalize-analysis-chunk and
 * kicks off Pegasus from the staged tmp file.
 */
async function predictPegasusEligibility(
  file: File,
): Promise<{ eligible: true } | { eligible: false; reason: string }> {
  // Audio files take the same analyze path (server's runPegasusOnTmpFile
  // branches on media_kind) but skip Pegasus itself — they're transcribed
  // and Claude-summarized for the Description sidecar. So they ARE
  // eligible for the analysis kick-off; only the duration/size video gates
  // get skipped.
  if (isAudioFile(file)) {
    return { eligible: true };
  }
  if (!file.type.startsWith("video/")) {
    return { eligible: false, reason: "not a video file" };
  }
  if (file.size > PEGASUS_MAX_BYTES) {
    return { eligible: false, reason: `exceeds 2 GB Pegasus limit` };
  }
  const duration = await probeVideoDurationSeconds(file);
  if (duration != null) {
    if (duration < PEGASUS_MIN_DURATION_SEC) {
      return { eligible: false, reason: `shorter than ${PEGASUS_MIN_DURATION_SEC}s` };
    }
    if (duration > PEGASUS_MAX_DURATION_SEC) {
      return {
        eligible: false,
        reason: `longer than ${PEGASUS_MAX_DURATION_SEC / 60 / 60}h`,
      };
    }
  }
  return { eligible: true };
}

/** Upload a text sidecar to a Google Drive folder via the existing
 *  consent-upload path. Returns the Drive file ID so callers (e.g. the
 *  per-clip Topic Tag review flow) can later rename the sidecar in place. */
async function uploadSidecarToDrive(
  filename: string,
  content: string,
  mimeType: string,
  driveFolderId: string,
): Promise<string> {
  const blob = new Blob([content], { type: mimeType });
  const form = new FormData();
  form.append("file", blob, filename);
  form.append("filename", filename);
  form.append("driveFolderId", driveFolderId);
  form.append("mimeType", mimeType);
  const res = await fetch("/api/consent-upload", { method: "POST", body: form });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error ?? `Sidecar upload failed (${res.status})`);
  }
  const body = (await res.json().catch(() => ({}))) as { fileId?: string };
  return body.fileId ?? "";
}

/**
 * Rename a file inside an FSA directory handle. Prefers the spec-aligned
 * FileSystemHandle.move() (Chromium 110+) — for older / non-Chromium engines
 * we fall back to copy-then-delete, which works but rewrites the file.
 * Throws if the source doesn't exist.
 */
async function renameLocalFile(
  parentDir: FileSystemDirectoryHandle,
  oldName: string,
  newName: string,
): Promise<void> {
  if (oldName === newName) return;
  const srcHandle = await parentDir.getFileHandle(oldName);
  const moveCapable = srcHandle as FileSystemFileHandle & {
    move?: (newName: string) => Promise<void>;
  };
  if (typeof moveCapable.move === "function") {
    await moveCapable.move(newName);
    return;
  }
  // Fallback: copy bytes to a new file, then delete the source.
  const file = await srcHandle.getFile();
  const destHandle = await parentDir.getFileHandle(newName, { create: true });
  const writable = await destHandle.createWritable();
  await writable.write(file);
  await writable.close();
  await parentDir.removeEntry(oldName);
}

/**
 * Pull the 4-digit year out of a date-folder name like "04.08.26" or
 * "04.08.26 - exam". Used by the Provider A-Roll upload flow to route
 * uploads into a year-wrapper subfolder under each provider folder.
 * Returns null for unrecognized formats; caller falls back to the
 * non-year-wrapped layout.
 *
 * Maps "YY" to "20YY" — works through 2099, plenty of runway.
 */
function yearFromDateFolder(folderName: string): string | null {
  const m = folderName.match(/^\d{2}\.\d{2}\.(\d{2})\b/);
  if (!m) return null;
  const yy = parseInt(m[1], 10);
  if (Number.isNaN(yy)) return null;
  return `20${yy.toString().padStart(2, "0")}`;
}

/** Swap the extension on a renamed filename — e.g. "Foo - tag.mp4" → "Foo - tag.txt". */
function withExtension(renamedName: string, newExt: string): string {
  const lastDot = renamedName.lastIndexOf(".");
  const base = lastDot > 0 ? renamedName.slice(0, lastDot) : renamedName;
  return `${base}.${newExt}`;
}

// ─── Direct browser → Drive upload ──────────────────────────────────────────
//
// The fast path: PUT chunks straight to Google's resumable session URL with no
// server in the byte path. Because the bytes go direct to Google (not through
// our reverse proxy), the 60s proxy read-timeout that forced 2 MB chunks no
// longer applies — we use large chunks to minimize round trips. The 308
// responses (and their Range header) are CORS-readable for progress + resume;
// only the final completion response isn't, so the fileId is resolved via the
// server-side /api/drive-upload/finalize status query.

/** 16 MB — multiple of 256 KB (Drive's resumable chunk requirement). */
const DIRECT_DRIVE_CHUNK_SIZE = 16 * 1024 * 1024;

/**
 * PUT one chunk directly to a Drive resumable session URL via XHR (XHR, not
 * fetch, so we get upload.onprogress). Resolves "incomplete" + the next byte
 * offset on a 308, or "complete" when Drive finishes. On the final chunk the
 * completion response is usually not CORS-readable, so if the request body
 * finished uploading we treat the resulting error as success and let /finalize
 * confirm. For non-final chunks an error is a real failure and rejects.
 */
function putDriveChunkDirect(
  uploadUrl: string,
  blob: Blob,
  contentRange: string,
  baseLoaded: number,
  totalBytes: number,
  isLast: boolean,
  onProgress?: (pct: number, loaded: number, total: number) => void,
): Promise<{ kind: "incomplete"; nextOffset: number } | { kind: "complete" }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.timeout = 10 * 60 * 1000; // generous: a big chunk on a slow uplink
    let uploadBodyFinished = false;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        const loaded = baseLoaded + e.loaded;
        onProgress(Math.min(99, Math.round((loaded / totalBytes) * 100)), loaded, totalBytes);
      }
    };
    xhr.upload.onload = () => { uploadBodyFinished = true; };
    xhr.onload = () => {
      if (xhr.status === 308) {
        const range = xhr.getResponseHeader("Range");
        const nextOffset = range ? parseInt(range.split("-")[1], 10) + 1 : baseLoaded + blob.size;
        resolve({ kind: "incomplete", nextOffset });
      } else if (xhr.status === 200 || xhr.status === 201) {
        resolve({ kind: "complete" });
      } else {
        reject(new Error(`Drive PUT failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      // Expected on the final chunk: Drive's completion response isn't
      // CORS-readable, so XHR surfaces a (status 0) error even though the bytes
      // landed. If the body finished, call it complete — /finalize is the
      // source of truth. Non-final chunks: a real network failure → retry.
      if (isLast && uploadBodyFinished) resolve({ kind: "complete" });
      else reject(new Error("Drive PUT network error"));
    };
    xhr.ontimeout = () => reject(new Error("Drive PUT timed out"));
    // Content-Range is a settable request header; Content-Length is set by the
    // browser from the blob.
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("Content-Range", contentRange);
    xhr.send(blob);
  });
}

/**
 * Upload a file to Drive directly from the browser, returning the Drive fileId.
 *   1. POST /init   → server mints the resumable session, returns its URL.
 *   2. PUT chunks   → straight to Google's session URL (no server byte-proxy).
 *   3. POST /finalize → server resolves the fileId via a status query.
 * Progress callback receives (pct 0-100, uploadedBytes, totalBytes).
 */
async function uploadFileToDriveBrowserDirect(
  file: File,
  fileName: string,
  driveFolderId: string,
  onProgress?: (pct: number, loaded: number, total: number) => void,
): Promise<string> {
  const totalBytes = file.size;
  const totalChunks = Math.max(1, Math.ceil(totalBytes / DIRECT_DRIVE_CHUNK_SIZE));

  // 1. Init — get the raw Drive resumable session URL.
  const initResp = await fetch("/api/drive-upload/init", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filename: fileName,
      mimeType: file.type || "application/octet-stream",
      totalBytes,
      totalChunks,
      driveFolderId,
    }),
  });
  if (!initResp.ok) {
    const err = await initResp.json().catch(() => ({ error: initResp.statusText }));
    throw new Error(`Drive init failed (${initResp.status}): ${err.error ?? initResp.statusText}`);
  }
  const { uploadId, uploadUrl } = (await initResp.json()) as { uploadId: string; uploadUrl?: string };
  if (!uploadUrl) throw new Error("Server did not return a direct upload URL");

  // 2. Stream chunks straight to Google, resuming from Drive's confirmed offset.
  let offset = 0;
  while (offset < totalBytes) {
    const start = offset;
    const end = Math.min(start + DIRECT_DRIVE_CHUNK_SIZE, totalBytes);
    const isLast = end >= totalBytes;
    const blob = file.slice(start, end);
    const contentRange = `bytes ${start}-${end - 1}/${totalBytes}`;

    const MAX_CHUNK_RETRIES = 3;
    let advanced = false;
    let lastErr: Error | null = null;
    for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
      try {
        const outcome = await putDriveChunkDirect(
          uploadUrl, blob, contentRange, start, totalBytes, isLast, onProgress,
        );
        offset = outcome.kind === "incomplete" ? outcome.nextOffset : totalBytes;
        advanced = true;
        break;
      } catch (err) {
        lastErr = err instanceof Error ? err : new Error(String(err));
        if (attempt < MAX_CHUNK_RETRIES) await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
    if (!advanced) throw lastErr ?? new Error("Drive chunk upload failed");
    // Guard against a malformed 308 Range that doesn't advance — without this a
    // stuck offset would spin forever.
    if (offset <= start) throw new Error("Drive upload stalled (offset did not advance)");
  }

  // 3. Finalize — server reads the fileId (the browser can't, post-completion).
  const finResp = await fetch("/api/drive-upload/finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uploadId }),
  });
  if (!finResp.ok) {
    const err = await finResp.json().catch(() => ({ error: finResp.statusText }));
    throw new Error(`Drive finalize failed (${finResp.status}): ${err.error ?? finResp.statusText}`);
  }
  const fin = (await finResp.json()) as { complete: boolean; fileId?: string; receivedBytes?: number };
  if (!fin.complete || !fin.fileId) {
    throw new Error(
      `Drive upload incomplete after all chunks (received ${fin.receivedBytes ?? "?"}/${totalBytes})`,
    );
  }
  onProgress?.(100, totalBytes, totalBytes);
  return fin.fileId;
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function FootageStep({ wizardState, onComplete, onBack, subjectKind = "patient" }: FootageStepProps) {
  const isProvider = subjectKind === "provider";
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  // Batch-level associated providers for the session. Seeded from the value
  // chosen on PatientStep, but editable here on the tagging screen so the user
  // can add/adjust the whole-session providers without going back. Read by the
  // footage.create + finalize calls, so edits made before upload apply to every
  // clip. Provider A-Roll never carries these.
  const [batchProviders, setBatchProviders] = useState<string[]>(
    isProvider ? [] : (wizardState.providers ?? []),
  );
  const [editingBatchProviders, setEditingBatchProviders] = useState(false);

  // Two-phase state machine
  // "adding"   → user is dropping files
  // "analyzing" → AI is processing all files
  // "adding" → user adds files and tags them
  // "uploading" → final upload in progress
  const [appPhase, setAppPhase] = useState<"adding" | "uploading">("adding");
  const [overallProgress, setOverallProgress] = useState(0);
  // Stores the processed file list after finalizeAll completes, so retryAllFailed can call onComplete
  const processedFilesRef = useRef<ProcessedFile[]>([]);
  /** Resolved Video Topic to ship with onComplete. Carries whatever the user
   *  typed on PatientStep (the folder-suffix umbrella label). Provider A-Roll
   *  only — patient flow leaves it undefined. */
  const resolvedTopicRef = useRef<string | undefined>(wizardState.videoTopic);

  // ── Post-upload per-file Topic Tag suggestion review dialog ──────────────
  // After all uploads + transcriptions land, any provider clip that the user
  // didn't tag manually gets a Bedrock-suggested Topic Tag. The dialog below
  // surfaces every suggestion at once so the user can edit / skip individual
  // rows, then accept all in a single click. Accepting renames the Drive
  // file, renames the local file (where FSA supports it), and updates the
  // DB row with the chosen tag.
  interface TagSuggestionRow {
    /** Index into the FootageStep `files` state */
    entryIndex: number;
    /** Drive file ID — null when the Drive upload itself failed */
    driveFileId: string | null;
    /** Drive file ID for the .txt sidecar (renamed alongside the video) */
    txtSidecarDriveFileId: string | null;
    /** Drive file ID for the .srt sidecar (renamed alongside the video) */
    srtSidecarDriveFileId: string | null;
    /** Date folder this file landed in (used to locate the local file handle) */
    dateFolder: string;
    /** Original filename, for display */
    originalName: string;
    /** Current renamed filename (before applying the suggestion) */
    currentRenamedName: string;
    /** DB row ID — finalizeFootage is re-called with this if accepted */
    dbFileId: number;
    /** Thumbnail data URL, if available */
    thumbnail?: string;
    /** Initial AI suggestion, kept around for display */
    suggested: string;
    /** Currently-edited tag in the input box */
    edited: string;
    /** User chose to skip this row */
    skipped: boolean;
  }
  const [tagReviewDialog, setTagReviewDialog] = useState<{
    open: boolean;
    rows: TagSuggestionRow[];
    /** Resolve callback the upload pipeline is awaiting. Receives the final
     *  rows the user accepted (with edits applied), or null on full skip. */
    resolve: ((value: TagSuggestionRow[] | null) => void) | null;
    /** True while suggestions are being fetched (dialog shows a spinner). */
    fetching: boolean;
  }>({ open: false, rows: [], resolve: null, fetching: false });
  /** While the review dialog is up, lock the underlying upload UI behind a
   *  full-screen overlay so users can't accidentally re-trigger work. */
  const [applyingTagSuggestions, setApplyingTagSuggestions] = useState(false);
  const suggestTopicTag = trpc.bedrock.suggestTopicTag.useMutation();
  const renameDriveFileMutation = trpc.gdrive.renameFile.useMutation();
  const ensureSidecarFolderMutation = trpc.gdrive.ensureSidecarFolder.useMutation();
  const ensureFolderMutation = trpc.gdrive.ensureFolder.useMutation();
  /** Tracks in-flight getFileCreationDate promises so finalizeAll can wait
   *  for all metadata reads to finish before grouping files by date.
   *  Without this, fast clicks on Upload would let files fall back to
   *  file.lastModified and end up in mismatched MM.DD.YY folders. */
  const detectionPromisesRef = useRef<Promise<unknown>[]>([]);
  const { setUploadActive, enqueuePegasusPoll } = useUploadContext();

  // (Storage health check removed — uploads stream directly to Google Drive.)

  // ── Per-file retry state (tracks which indices are currently retrying) ────
  const [retryingIndices, setRetryingIndices] = useState<Set<number>>(new Set());

  // ── Per-file skip confirmation state ─────────────────────────────────────
  // Holds the index of the file pending skip confirmation, or null if no dialog is open.
  const [skipConfirmIndex, setSkipConfirmIndex] = useState<number | null>(null);
  // Tracks files whose post-skip analysis pipeline (finalize + local Pegasus)
  // has already been kicked off, so a re-click can't double-fire and a racing
  // main-loop completion doesn't repeat work.
  const skipAnalysisFiredRef = useRef<Set<number>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);

  const trpcUtils = trpc.useUtils();
  const createFootage = trpc.footage.create.useMutation();
  const finalizeFootage = trpc.footage.finalize.useMutation();

  // ── Tag library context (varies by subject kind) ─────────────────────────
  // Provider uploads tag against the Topic library; patient uploads against
  // the Activity library. We fetch the current library list so we can detect
  // brand-new tags on Upload click and prompt the user to confirm them.
  const libraryQuery = (isProvider ? trpc.topicTags.list : trpc.tags.list).useQuery();
  const libraryTags = libraryQuery.data ?? [];
  const registerActivityTagMutation = trpc.tags.registerWithDescription.useMutation();
  const registerTopicTagMutation = trpc.topicTags.registerWithDescription.useMutation();
  const registerNewTagMutation = isProvider ? registerTopicTagMutation : registerActivityTagMutation;
  // Used to create the MM.DD.YY/ Drive subfolder on demand inside finalizeAll
  // (one folder per distinct file-creation date in the batch).
  const createPatientFolders = trpc.gdrive.createPatientFolders.useMutation();
  // The post-S3 `gdrive.uploadFile` mutation is gone — chunks now stream
  // directly to Drive via uploadFileToDriveDirectly below.

  /**
   * Choose the upload path. Direct browser→Drive by default (fast, no server
   * byte-proxy); the legacy server-proxied chunk path when
   * USE_DIRECT_DRIVE_UPLOAD is disabled (rollback lever). Both return the Drive
   * fileId and report progress identically, so call sites don't change.
   */
  const uploadFileToDriveDirectly = (
    file: File,
    fileName: string,
    driveFolderId: string,
    onProgress?: (pct: number, loaded: number, total: number) => void,
  ): Promise<string> =>
    USE_DIRECT_DRIVE_UPLOAD
      ? uploadFileToDriveBrowserDirect(file, fileName, driveFolderId, onProgress)
      : uploadFileToDriveViaProxy(file, fileName, driveFolderId, onProgress);

  /**
   * Legacy path: stream the file to our server in 2 MB chunks, which proxies
   * each one to Drive. Kept as a fallback behind USE_DIRECT_DRIVE_UPLOAD.
   * Progress callback receives (pct 0-100, uploadedBytes, totalBytes).
   */
  const uploadFileToDriveViaProxy = async (
    file: File,
    fileName: string,
    driveFolderId: string,
    onProgress?: (pct: number, loaded: number, total: number) => void
  ): Promise<string> => {
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB per chunk (keeps requests under Cloud Run 60s timeout)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // 1. Init the Drive resumable session
    const initResp = await fetch("/api/drive-upload/init", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: fileName,
        mimeType: file.type || "application/octet-stream",
        totalBytes: file.size,
        totalChunks,
        driveFolderId,
      }),
    });
    if (!initResp.ok) {
      const err = await initResp.json().catch(() => ({ error: initResp.statusText }));
      throw new Error(`Drive init failed (${initResp.status}): ${err.error ?? initResp.statusText}`);
    }
    const { uploadId: driveUploadId } = await initResp.json() as { uploadId: string };

    // 2. Send each chunk
    let bytesUploaded = 0;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const MAX_CHUNK_RETRIES = 3;
      let chunkAccepted = false;
      for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
        const formData = new FormData();
        formData.append("file", chunk, fileName);
        formData.append("uploadId", driveUploadId);
        formData.append("chunkIndex", String(i));
        formData.append("totalChunks", String(totalChunks));

        try {
          const result = await new Promise<{ done: boolean; fileId?: string; received?: number; total?: number }>(
            (resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open("POST", "/api/drive-upload/chunk");
              xhr.timeout = 5 * 60 * 1000;
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) {
                  const loaded = bytesUploaded + e.loaded;
                  const pct = Math.round((loaded / file.size) * 100);
                  onProgress(Math.min(99, pct), loaded, file.size);
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  try { resolve(JSON.parse(xhr.responseText)); }
                  catch { reject(new Error("Chunk response parse failed")); }
                } else {
                  let msg = xhr.responseText;
                  try { msg = JSON.parse(xhr.responseText).error ?? msg; } catch { /* keep raw */ }
                  reject(new Error(`Drive chunk failed (${xhr.status}): ${msg}`));
                }
              };
              xhr.onerror = () => reject(new Error("Drive chunk network error"));
              xhr.ontimeout = () => reject(new Error("Drive chunk timed out"));
              xhr.send(formData);
            }
          );

          bytesUploaded += (end - start);
          onProgress?.(Math.min(99, Math.round((bytesUploaded / file.size) * 100)), bytesUploaded, file.size);

          if (result.done && result.fileId) {
            onProgress?.(100, file.size, file.size);
            return result.fileId;
          }
          chunkAccepted = true;
          break; // chunk accepted (308), move to next
        } catch (err) {
          if (attempt === MAX_CHUNK_RETRIES) throw err; // propagate on final attempt
          // Exponential backoff before retry
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
      if (!chunkAccepted) throw new Error(`Drive chunk ${i} failed after ${MAX_CHUNK_RETRIES} attempts`);
    }
    throw new Error("Drive upload completed all chunks but no fileId returned");
  };

  // ── File management ──────────────────────────────────────────────────────

  const addFiles = useCallback((newFiles: File[]) => {
    const entries: FileEntry[] = newFiles.map((file) => ({
      file,
      phase: "tagging",
      originalName: file.name,
      activities: [],
      // Always run through buildRenamedName so non-numeric prefixes (e.g. "IMG_") are
      // stripped from the start, even when no activity tags are added.
      // e.g. IMG_777.MOV → 777.MOV, IMG_E777.MOV → E777.MOV
      renamedName: buildRenamedName(file.name, []),
      progress: 0,
    }));
    setFiles((prev) => {
      const startIndex = prev.length;
      // Kick off async thumbnail extraction + metadata-date detection for each new file
      entries.forEach((_, relIdx) => {
        const absIdx = startIndex + relIdx;
        const file = newFiles[relIdx];
        // Audio files have nothing to render — VideoThumbnail's icon
        // placeholder takes over via isAudio={true}. Skip extraction.
        const extractor = isAudioFile(file)
          ? Promise.resolve(undefined as unknown as ThumbnailResult)
          : isImageFile(file)
            ? extractImageThumbnail(file)
            : extractFirstFrame(file);
        extractor.then((thumb) => {
          setFiles((cur) => cur.map((f, i) => i === absIdx ? {
            ...f,
            thumbnail: thumb?.dataUrl,
            thumbnailWidth: thumb?.width,
            thumbnailHeight: thumb?.height,
          } : f));
        });
        // Metadata date detection runs independently. Reads only the bytes
        // needed (MP4 box headers / EXIF segment) — cheap for large videos.
        // The promise is also tracked in detectionPromisesRef so finalizeAll
        // can await all pending detections before computing per-date groups
        // (otherwise fast-click uploaders end up with files routed to
        // file.lastModified folders that don't match the actual capture date).
        const detectionPromise = getFileCreationDate(file).then((detected) => {
          setFiles((cur) => cur.map((f, i) => i === absIdx ? {
            ...f,
            detectedDate: formatDateFolderFromMetadata(detected.date),
            detectedDateSource: detected.source,
          } : f));
        }).catch(() => {
          // Hard failure: leave detectedDate undefined; finalizeAll() will
          // fall back to file.lastModified before upload.
        });
        detectionPromisesRef.current.push(detectionPromise);
      });
      return [...prev, ...entries];
    });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) addFiles(droppedFiles);
    },
    [addFiles]
  );

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) addFiles(selected);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFile = (index: number, updates: Partial<FileEntry>) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));
  };

  const updateActivities = (index: number, activities: string[]) => {
    setFiles((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        return {
          ...f,
          activities,
          // Any manual tag edit is an explicit action, so it clears an earlier
          // "Skip Tag". If they end up empty again they can re-press Skip.
          tagSkipped: false,
          renamedName: buildRenamedName(f.originalName, activities),
        };
      })
    );
  };

  // Toggle "Skip Tag" for a clip. Marks the clip as intentionally untagged so
  // it uploads with the original-filename convention and is excluded from the
  // untagged-clips warning at finalize.
  const toggleSkipTag = (index: number) => {
    setFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, tagSkipped: !f.tagSkipped } : f)),
    );
  };

  // Per-clip provider additions (patient flow). These are ADDITIVE to the
  // session-level batch providers — the finalize write sends the union.
  const updateProviders = (index: number, providers: string[]) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, providers } : f)));
  };

  // Effective provider list for a clip at finalize time: batch ∪ per-clip
  // additions (deduped, case-insensitive). Always reflects the CURRENT batch
  // selection, so edits made to the batch on the tagging screen apply at
  // finalize too — not just the value stamped at create. Returns undefined
  // only when the union is empty (nothing to write). Patient flow only.
  const effectiveProvidersFor = (entry: FileEntry): string[] | undefined => {
    if (isProvider) return undefined;
    const merged = [...batchProviders];
    for (const p of entry.providers ?? []) {
      if (!merged.some((m) => m.toLowerCase() === p.toLowerCase())) merged.push(p);
    }
    return merged.length > 0 ? merged : undefined;
  };

  /** Retry a failed Google Drive transfer for a single file without re-uploading from device. */
  const retryDriveUpload = async (index: number) => {
    const entry = files[index];
    // Resolved per-file folder ID (set during finalizeAll) takes precedence;
    // fall back to wizardState's value for the session-resume / pre-set case.
    const driveFolderId = entry.resolvedDriveFolderId || wizardState.gDriveDateFolderId;
    if (!entry.file || !driveFolderId) {
      toast.error("Cannot retry — missing Drive destination or file");
      return;
    }
    updateFile(index, { drivePhase: "uploading", driveProgress: 0 });
    try {
      const fileName = entry.renamedName || entry.originalName;
      await uploadFileToDriveDirectly(
        entry.file,
        fileName,
        driveFolderId,
        (pct, loaded, total) => {
          updateFile(index, { driveProgress: pct, driveBytesLoaded: loaded, driveTotalBytes: total });
        }
      );
      updateFile(index, { drivePhase: "done", driveProgress: 100 });
      toast.success(`"${fileName}" uploaded to Google Drive`);
      // Check if this was the last failing Drive upload — if so, auto-navigate to Confirmation.
      // Guards:
      //  1. Don't navigate while the proceed-anyway dialog is open (user hasn't decided yet).
      //  2. Don't navigate while retryAllFailed is running (other files may still be uploading).
      //  3. Don't navigate if any file is still actively uploading (drivePhase === "uploading").
      const remainingDriveFailures = files.filter((f, i) => i !== index && f.drivePhase === "error").length;
      const remainingDriveUploading = files.filter((f, i) => i !== index && f.drivePhase === "uploading").length;
      const remainingS3Failures = files.filter((f) => f.phase === "error").length;
      if (
        remainingDriveFailures === 0 &&
        remainingDriveUploading === 0 &&
        !isRetryingAll &&
        !proceedAnywayOpen &&
        processedFilesRef.current.length > 0
      ) {
        if (remainingS3Failures > 0) {
          // S3 failures still present — open the proceed-anyway dialog instead of navigating
          setTimeout(() => setProceedAnywayOpen(true), 400);
        } else {
          setTimeout(() => onComplete(processedFilesRef.current, resolvedTopicRef.current), 800);
        }
      }
    } catch (err: any) {
      console.warn("[GDrive] Retry failed:", err);
      updateFile(index, { drivePhase: "error" });
      const reason = err?.message?.includes("timed out") ? " (timed out)" : "";
      toast.error(`Retry failed for "${entry.renamedName || entry.originalName}"${reason} — you can try again`);
    }
  };

  const [isRetryingAll, setIsRetryingAll] = useState(false);

  /** Retry all clips that have a Drive failure simultaneously. */
  const retryAllFailed = async () => {
    const failedIndices = files
      .map((f, i) => ({ f, i }))
      .filter(({ f }) => f.drivePhase === "error")
      .map(({ i }) => i);
    if (failedIndices.length === 0) return;
    if (!wizardState.gDriveDateFolderId) {
      toast.error("Cannot retry — missing Drive destination");
      return;
    }
    setIsRetryingAll(true);
    let succeeded = 0;
    let failed = 0;
    // Run retries sequentially — one at a time to avoid Drive rate limits
    for (const index of failedIndices) {
      const entry = files[index];
      if (!entry.file) {
        updateFile(index, { drivePhase: "error" });
        failed++;
        continue;
      }
      updateFile(index, { drivePhase: "uploading", driveProgress: 0 });
      try {
        const fileName = entry.renamedName || entry.originalName;
        await uploadFileToDriveDirectly(
          entry.file,
          fileName,
          wizardState.gDriveDateFolderId!,
          (pct, loaded, total) => {
            updateFile(index, { driveProgress: pct, driveBytesLoaded: loaded, driveTotalBytes: total });
          }
        );
        updateFile(index, { drivePhase: "done", driveProgress: 100 });
        succeeded++;
      } catch {
        updateFile(index, { drivePhase: "error" });
        failed++;
      }
    }
    if (failed === 0) {
      toast.success(`All ${succeeded} file${succeeded !== 1 ? "s" : ""} uploaded to Google Drive`);
      if (processedFilesRef.current.length > 0) {
        const s3StillFailed = files.filter((f) => f.phase === "error").length;
        if (s3StillFailed > 0) {
          // S3 failures still present — show the dialog (close and reopen to refresh the list)
          setProceedAnywayOpen(false);
          setTimeout(() => setProceedAnywayOpen(true), 400);
        } else {
          // All uploads succeeded — close any open dialog and navigate
          setProceedAnywayOpen(false);
          setTimeout(() => onComplete(processedFilesRef.current, resolvedTopicRef.current), 800);
        }
      }
    } else if (succeeded > 0) {
      toast.warning(`${succeeded} uploaded, ${failed} still failed — retry the remaining files before continuing`);
    } else {
      toast.error(`All ${failed} retries failed — check your Drive connection and try again`);
    }
    setIsRetryingAll(false);
  };

  /**
   * Immediately mark a file as skipped (called after user confirms the skip dialog).
   * Skipped files are treated like done files for the purpose of navigation:
   * they don't block the proceed-anyway dialog or the success screen.
   *
   * The user only sees this button when they want to abandon the Drive upload —
   * but they still want the file searchable. So we proactively finalize the DB
   * row and kick the local-stream-to-server Pegasus path (which handles both
   * transcript and ai_description on the server), guaranteeing the sidecar
   * data lands even if Drive never finishes.
   */
  const skipFile = (index: number) => {
    const entry = files[index];
    updateFile(index, { phase: "skipped", progress: 0, error: undefined, errorReason: undefined });
    setSkipConfirmIndex(null);

    // ── Sidecar guarantee ────────────────────────────────────────────────
    // Fire-and-forget: finalize the DB row (so it's searchable with the
    // user's tags) and stream the file to /api/finalize-analysis-chunk so
    // Pegasus + Whisper run server-side. Both endpoints are idempotent
    // enough that a race with the main upload loop is harmless.
    if (entry && entry.id && !skipAnalysisFiredRef.current.has(index)) {
      skipAnalysisFiredRef.current.add(index);
      const fileId = entry.id;
      const renamedName = entry.renamedName || buildRenamedName(entry.originalName, entry.activities);
      void (async () => {
        try {
          await finalizeFootage.mutateAsync({
            fileId,
            activities: entry.activities,
            renamedName,
            driveFolderId: wizardState.driveFolderId ?? undefined,
            driveFileId: entry.driveFileId,
            tagKind: isProvider ? "topic" : "activity",
            providers: effectiveProvidersFor(entry),
          });
        } catch (err: any) {
          console.warn("[skip] finalize on skip failed:", err?.message ?? err);
        }
        try {
          const eligibility = await predictPegasusEligibility(entry.file);
          if (eligibility.eligible) {
            void uploadVideoForLocalPegasus(entry.file, fileId).catch((err) => {
              console.warn("[skip] local-pegasus fire-and-forget errored:", err);
            });
          }
        } catch (err: any) {
          console.warn("[skip] pegasus eligibility probe failed:", err?.message ?? err);
        }
      })();
    }

    // Check if all files are now in a terminal state (done / skipped / error)
    // and trigger completion if appropriate
    setTimeout(() => {
      setFiles((prev) => {
        const allTerminal = prev.every(
          (f) => f.phase === "done" || f.phase === "skipped" || f.phase === "error"
        );
        const anyError = prev.some((f) => f.phase === "error");
        const anySkipped = prev.some((f) => f.phase === "skipped");
        if (allTerminal) {
          // If processedFilesRef is already populated (upload ran), use it.
          // Otherwise build a processedFiles list from the current file state
          // (this handles the case where the user skips before/during upload
          // without ever completing finalizeAll, e.g. single-file skip).
          if (processedFilesRef.current.length === 0) {
            const builtFiles: ProcessedFile[] = prev
              .filter((f) => f.phase === "done" || f.phase === "skipped")
              .map((f) => ({
                id: f.id ?? -1,
                originalName: f.originalName,
                renamedName: f.renamedName || f.originalName,
                activities: f.activities,
                driveFileId: f.driveFileId,
                status: f.phase === "skipped" ? "skipped" : "uploaded",
                thumbnail: f.thumbnail,
                thumbnailWidth: f.thumbnailWidth,
                thumbnailHeight: f.thumbnailHeight,
                dateFolder: f.resolvedDateFolder,
              } as ProcessedFile));
            processedFilesRef.current = builtFiles;
          }
          if (anyError || anySkipped) {
            // Show the proceed-anyway dialog listing failures + skips
            if (processedFilesRef.current.length > 0 || anyError) {
              setAppPhase("adding");
              setUploadActive(false);
              setProceedAnywayOpen(true);
            } else {
              // All files errored with no successes — just go back to adding
              setAppPhase("adding");
              setUploadActive(false);
            }
          } else {
            // All done — navigate directly
            if (processedFilesRef.current.length > 0) {
              setUploadActive(false);
              onComplete(processedFilesRef.current, resolvedTopicRef.current);
            }
          }
        }
        return prev; // no state change — we're just reading
      });
    }, 200);
  };

  // Legacy S3 chunked-upload helper. The Manus Forge S3 proxy has been removed
  // from the backend, so this function is dead code kept only to satisfy any
  // remaining callers during the cut-over. New code paths must use
  // `uploadFileToDriveDirectly` above. Calling this helper now throws.
  const uploadFileToStorage = (
    _file: File,
    _driveFolderId: string,
    _sessionId: number,
    _onProgress?: (pct: number, loaded?: number, total?: number) => void
  ): Promise<{ url: string; key: string }> => {
    return Promise.reject(new Error(
      "uploadFileToStorage is no longer supported — use uploadFileToDriveDirectly. " +
        "If you see this error, an upload path is still routing through the removed S3 proxy."
    ));
  };
  // ── Phase 1: Analyze all files ────────────────────────────────────────────



  // ── No-tag confirmation dialog ──────────────────────────────────────────────
  const [noTagDialog, setNoTagDialog] = useState<{ open: boolean; onConfirm: () => void }>({ open: false, onConfirm: () => {} });

  // ── New-tag confirmation dialog ─────────────────────────────────────────────
  // Shown when the user has typed one or more tags that aren't yet in the
  // active library (Activity for patients, Topic for providers). The dialog
  // lets them edit the name (e.g. fix a typo) and add an optional description
  // before the tag lands in the library.
  interface NewTagEntry {
    /** Tag as originally typed — used to find/replace inside file activities if edited */
    originalName: string;
    /** Current (potentially edited) name */
    name: string;
    /** Optional description */
    description: string;
    /** Indices of files whose activities array contains this tag */
    fileIndices: number[];
  }
  const [newTagDialog, setNewTagDialog] = useState<{ open: boolean; entries: NewTagEntry[]; submitting: boolean }>(
    { open: false, entries: [], submitting: false },
  );

  // Sentinel: bumping this triggers a useEffect that re-runs the finalize
  // gate after React has committed any file-activity rewrites from the
  // new-tag dialog. Avoids stale-closure issues with synchronous calls.
  const [pendingFinalizeTick, setPendingFinalizeTick] = useState(0);

  // ── Proceed-anyway dialog (when Drive uploads have failures) ─────────────────
  const [proceedAnywayOpen, setProceedAnywayOpen] = useState(false);

  // ── Proceed-anyway dialog (when S3 uploads have failures) ──────────────────
  const [s3ProceedAnywayOpen, setS3ProceedAnywayOpen] = useState(false);

  // ── Finalize: upload all tagged files ────────────────────────────────────────────────

  /** Walks the current file list and the no-tag gate, then either opens the
   *  no-tag warning dialog or kicks off finalizeAll. Pulled out so it can be
   *  re-entered after the new-tag confirmation dialog closes. */
  const proceedPastNewTagGate = () => {
    const reviewable = files.filter((f) => (f.phase === "tagging" || f.phase === "error") && !isImageFile(f.file));
    // Clips the operator explicitly skipped are intentionally blank — don't warn on them.
    const noTagCount = reviewable.filter((f) => f.activities.length === 0 && !f.tagSkipped).length;
    if (noTagCount > 0) {
      setNoTagDialog({ open: true, onConfirm: () => { setNoTagDialog((d) => ({ ...d, open: false })); finalizeAll(); } });
    } else {
      finalizeAll();
    }
  };

  // Drive the deferred finalize: when the new-tag dialog applies edits via
  // setFiles, we bump pendingFinalizeTick and let this effect run after the
  // commit so the gate functions see fresh file state.
  useEffect(() => {
    if (pendingFinalizeTick > 0) {
      proceedPastNewTagGate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFinalizeTick]);

  // Pre-warm ffmpeg.wasm as soon as we know transcription is coming, so the
  // first clip doesn't pay the ~31 MB core.wasm download as part of its
  // critical path. Triggers:
  //   - Provider A-Roll: on mount (every clip transcribes).
  //   - Patient B-Roll: the moment any clip's tag list contains an
  //     "Explanation" or "Discussion" tag — the only tags that trigger
  //     transcription on the patient flow. Avoids spending the ~31 MB
  //     bandwidth on sessions where the user never tags for transcription.
  // prewarmFFmpeg's underlying singleton dedupes repeat calls so this
  // effect can re-fire safely whenever the file list changes.
  useEffect(() => {
    if (isProvider) {
      prewarmFFmpeg();
      return;
    }
    if (files.some((f) => activitiesTriggerTranscription(f.activities))) {
      prewarmFFmpeg();
    }
  }, [isProvider, files]);

  /** Called when the user clicks "Upload & Rename All". First checks whether
   *  any file uses a tag that isn't in the library yet; if so, opens the
   *  new-tag confirmation dialog. Otherwise falls through to the no-tag gate. */
  const handleFinalizeClick = () => {
    const reviewable = files.filter((f) => f.phase === "tagging" || f.phase === "error");

    // Detect tags that are not in the active library. Match case-insensitively
    // since the library is case-insensitive in practice (search uses ILIKE).
    const libraryNamesLower = new Set(libraryTags.map((t) => t.name.toLowerCase()));
    const newTagsMap = new Map<string, { originalName: string; fileIndices: number[] }>();
    files.forEach((entry, idx) => {
      if (entry.phase !== "tagging" && entry.phase !== "error") return;
      for (const tag of entry.activities) {
        const trimmed = tag.trim();
        if (!trimmed) continue;
        const lower = trimmed.toLowerCase();
        if (libraryNamesLower.has(lower)) continue;
        const existing = newTagsMap.get(lower);
        if (existing) {
          existing.fileIndices.push(idx);
        } else {
          newTagsMap.set(lower, { originalName: trimmed, fileIndices: [idx] });
        }
      }
    });

    if (newTagsMap.size > 0) {
      const entries: NewTagEntry[] = Array.from(newTagsMap.values()).map((v) => ({
        originalName: v.originalName,
        name: v.originalName,
        description: "",
        fileIndices: v.fileIndices,
      }));
      setNewTagDialog({ open: true, entries, submitting: false });
      return;
    }

    proceedPastNewTagGate();
  };

  /** Apply the new-tag dialog: rewrite any edited names in their owning
   *  files, register each tag (with optional description) in the right
   *  library, then continue with the upload. */
  const handleConfirmNewTags = async () => {
    const { entries } = newTagDialog;
    // Validate names
    const cleaned = entries.map((e) => ({ ...e, name: e.name.trim() }));
    if (cleaned.some((e) => !e.name)) {
      toast.error("Tag names cannot be empty");
      return;
    }
    const lowerNames = cleaned.map((e) => e.name.toLowerCase());
    if (new Set(lowerNames).size !== lowerNames.length) {
      toast.error("Two new tags ended up with the same name — please differentiate them.");
      return;
    }

    setNewTagDialog((d) => ({ ...d, submitting: true }));
    try {
      // Build a rename map keyed by the lowercased original name so we can
      // rewrite all affected files in a single setFiles pass.
      const renameMap = new Map<string, string>();
      for (const e of cleaned) {
        if (e.name.toLowerCase() !== e.originalName.toLowerCase()) {
          renameMap.set(e.originalName.toLowerCase(), e.name);
        }
      }
      if (renameMap.size > 0) {
        setFiles((prev) =>
          prev.map((f) => {
            let changed = false;
            const newActivities = f.activities.map((a) => {
              const replacement = renameMap.get(a.toLowerCase());
              if (replacement && replacement !== a) {
                changed = true;
                return replacement;
              }
              return a;
            });
            if (!changed) return f;
            return {
              ...f,
              activities: newActivities,
              renamedName: buildRenamedName(f.originalName, newActivities),
            };
          }),
        );
      }

      // Register each tag in the library (idempotent on the server).
      await Promise.all(
        cleaned.map((e) =>
          registerNewTagMutation.mutateAsync({
            name: e.name,
            description: e.description.trim() || undefined,
          }),
        ),
      );

      // Refresh library so subsequent Upload clicks don't re-flag these tags.
      if (isProvider) {
        await trpcUtils.topicTags.list.invalidate();
      } else {
        await trpcUtils.tags.list.invalidate();
      }

      const newCount = cleaned.length;
      toast.success(
        `Added ${newCount} ${newCount === 1 ? "tag" : "tags"} to the ${isProvider ? "Topic" : "Activity"} Library`,
      );

      setNewTagDialog({ open: false, entries: [], submitting: false });
      // Trigger the deferred finalize via useEffect so it reads fresh state.
      setPendingFinalizeTick((n) => n + 1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to add tags to library";
      toast.error(message);
      setNewTagDialog((d) => ({ ...d, submitting: false }));
    }
  };

  const finalizeAll = async () => {
    // Wait for any in-flight metadata-date detections to finish before
    // grouping files by date. Without this, fast-click uploaders can land
    // some files in their detected-date folder and others in their
    // file.lastModified folder, splitting one session across the wrong
    // MM.DD.YY folders. Each individual detection is fast (KB-level reads),
    // so this is usually a no-op by the time the user has finished tagging.
    if (detectionPromisesRef.current.length > 0) {
      await Promise.all(detectionPromisesRef.current);
    }

    // Include ALL tagging-phase and error-phase files (zero-tag files upload with original filename)
    const reviewable = files.filter((f) => f.phase === "tagging" || f.phase === "error");
    if (reviewable.length === 0) { toast.error("No files ready to upload"); return; }

    setAppPhase("uploading");
    setUploadActive(true); // prevent auto-logout during upload
    // Resync the resolved-topic ref to the latest wizardState value in case
    // the user went back to PatientStep and edited the field. The
    // suggestion modal may later overwrite this.
    resolvedTopicRef.current = wizardState.videoTopic;
    const processedFiles: ProcessedFile[] = [];
    /** Provider A-Roll only: clips that finished uploading + transcribing with
     *  zero manually-assigned Topic Tags. Collected here and fed into the
     *  post-upload review dialog so each clip gets its own AI-suggested tag.
     *  Patient flow doesn't populate this. */
    interface TagSuggestionCandidate {
      entryIndex: number;
      transcript: string;
      driveFileId: string | null;
      /** Drive file ID for the .txt sidecar (null when Drive upload failed). */
      txtSidecarDriveFileId: string | null;
      /** Drive file ID for the .srt sidecar (null when Drive upload failed). */
      srtSidecarDriveFileId: string | null;
      dateFolder: string;
      originalName: string;
      currentRenamedName: string;
      dbFileId: number;
      thumbnail?: string;
    }
    const tagSuggestionCandidates: TagSuggestionCandidate[] = [];

    // ── Per-batch Pegasus-skip tracking ────────────────────────────────────
    // Files where we predicted Pegasus would reject (or where Drive isn't a
    // destination) end up here so we can surface a single end-of-batch toast
    // explaining what missed an AI description. Per-file toasts would spam.
    const pegasusSkips: Array<{ name: string; reason: string }> = [];

    // ── Per-session sidecar-subfolder caches ───────────────────────────────
    // Sidecars (transcripts + descriptions) live in a "Ds+Ts" subfolder
    // next to the video. Resolve / create that folder once per date folder
    // (or per local handle) and reuse — avoids one round-trip per sidecar.
    const driveSidecarFolderCache = new Map<string, Promise<string | null>>();
    const localSidecarFolderCache = new Map<
      FileSystemDirectoryHandle,
      Promise<FileSystemDirectoryHandle | null>
    >();
    const resolveDriveSidecarFolder = (parentFolderId: string): Promise<string | null> => {
      const cached = driveSidecarFolderCache.get(parentFolderId);
      if (cached) return cached;
      const promise = ensureSidecarFolderMutation
        .mutateAsync({ parentFolderId })
        .then((r) => r.folderId)
        .catch((err) => {
          console.warn("[sidecar] Drive Ds+Ts resolve failed:", err?.message ?? err);
          return null;
        });
      driveSidecarFolderCache.set(parentFolderId, promise);
      return promise;
    };
    const resolveLocalSidecarFolder = (
      parentHandle: FileSystemDirectoryHandle,
    ): Promise<FileSystemDirectoryHandle | null> => {
      const cached = localSidecarFolderCache.get(parentHandle);
      if (cached) return cached;
      const promise = ensureLocalSidecarSubfolder(parentHandle);
      localSidecarFolderCache.set(parentHandle, promise);
      return promise;
    };

    // ── Resolve each file's MM.DD.YY folder ────────────────────────────────
    // In the auto-detect-from-metadata flow (wizardState.dateFolder === ""),
    // every file routes to a folder named after its own embedded creation
    // date — so one batch may produce multiple date folders. The session
    // resume / pre-set case keeps the old behavior: all files route to the
    // single dateFolder that was fixed earlier.
    //
    // Suffix precedence: Provider A-Roll's free-text videoTopic wins over the
    // patient-flow isExam toggle (the two are mutually exclusive in practice
    // — provider hides the Exam toggle and patient hides the Topic field).
    const examSuffix = buildDateFolderSuffix(wizardState);
    const presetDateFolder = wizardState.dateFolder && wizardState.dateFolder.length > 0
      ? wizardState.dateFolder
      : null;

    /** Returns the MM.DD.YY[ - exam] folder name this entry belongs in. */
    const folderForEntry = (entry: FileEntry): string => {
      if (presetDateFolder) return presetDateFolder; // resume / explicit override
      const detected =
        entry.overrideDate ||
        entry.detectedDate ||
        formatDateFolderFromMetadata(new Date(entry.file.lastModified));
      return `${detected}${examSuffix}`;
    };

    // Stamp each reviewable entry with its target folder so UI + retry can read it.
    {
      const folderByIndex = new Map<number, string>();
      files.forEach((entry, i) => {
        if (entry.phase === "tagging" || entry.phase === "error") {
          folderByIndex.set(i, folderForEntry(entry));
        }
      });
      setFiles((prev) => prev.map((f, i) => folderByIndex.has(i)
        ? { ...f, resolvedDateFolder: folderByIndex.get(i) }
        : f));
    }

    // ── Final clip names, unique within each date folder ────────────────────
    // Long numbers are cut to their last 4 digits, so two clips in a batch
    // can land on the same name (two DJI clips both ending "…920"). Saving
    // both to a local folder would overwrite the first, so clashes get a
    // wider number (or " (2)") here, before anything is written.
    const finalNameByIndex = assignUniqueNames(
      files.flatMap((entry, i) =>
        entry.phase === "tagging" || entry.phase === "error"
          ? [{
              key: i,
              folder: folderForEntry(entry),
              originalName: entry.originalName,
              activities: entry.activities,
              currentName: entry.renamedName,
            }]
          : [],
      ),
    );
    setFiles((prev) => prev.map((f, i) => {
      const name = finalNameByIndex.get(i);
      return name && name !== f.renamedName ? { ...f, renamedName: name } : f;
    }));

    // ── Per-date-folder caches ─────────────────────────────────────────────
    // Lazily create the FileSystemDirectoryHandle (local) and Drive folder
    // ID for each distinct date the first time we encounter it, then reuse.
    //
    // CRITICAL: caches store the in-flight Promise, not the resolved value.
    // finalizeAll's upload loop runs with concurrency 3, so multiple files
    // for the same date hit resolve* in parallel before any of them has
    // populated the cache. Caching the promise instead means the first
    // caller registers it synchronously and every concurrent caller
    // awaits the same creation attempt. Without this, Drive happily
    // creates one folder per parallel call (it doesn't dedupe by name)
    // and files end up scattered across 2-3 identically-named folders.
    const localFolderCache = new Map<string, Promise<FileSystemDirectoryHandle | null>>();
    const driveFolderCache = new Map<string, Promise<string | undefined>>();
    // Year-folder cache, used by the Provider A-Roll flow to lazily
    // create <provider>/<year>/ wrappers based on each file's date
    // metadata. Keyed by 4-digit year — every clip from 2026 reuses
    // the same year-folder lookup. Cache the in-flight promise (not
    // the resolved value) so concurrent uploads share one creation
    // attempt and Drive doesn't end up with duplicate "2026" folders.
    const driveYearFolderCache = new Map<string, Promise<string | undefined>>();
    // Patients get the same <Name>/<YYYY>/<MM.DD.YY> layout once an admin
    // turns on "Year folders for new uploads" (after the one-time reorg).
    // Fetched fresh per batch so a just-flipped switch applies immediately.
    const patientYearFolders =
      !isProvider &&
      (await trpcUtils.gdrive.yearFoldersEnabled.fetch().catch(() => false)) === true;
    const localYearFolderCache = new Map<string, Promise<FileSystemDirectoryHandle | null>>();
    // Seed Drive cache when we already have a folder ID from session-resume
    // or the new-patient flow — avoids re-creating the same folder.
    if (presetDateFolder && wizardState.gDriveDateFolderId) {
      driveFolderCache.set(presetDateFolder, Promise.resolve(wizardState.gDriveDateFolderId));
    }

    const baseLocalHandle = wizardState.patientFolderHandle ?? wizardState.localFolderHandle;
    const supportsFsa =
      !!baseLocalHandle &&
      typeof baseLocalHandle.getDirectoryHandle === "function" &&
      typeof (window as Window & { showDirectoryPicker?: unknown }).showDirectoryPicker === "function";

    // ── FileSystem Access permission gate ─────────────────────────────────
    // Handles persisted in IndexedDB lose their write permission on every
    // page reload (e.g. "Add more this session" navigates fresh). The
    // browser only allows requestPermission() inside a user gesture — and
    // the Upload button click that triggers finalizeAll IS such a gesture,
    // so this is the right moment to re-grant. Without this, the local
    // path silently falls through to triggerDownload() and files dump into
    // the OS Downloads folder.
    let localPermissionGranted = supportsFsa;
    if (supportsFsa && baseLocalHandle) {
      try {
        const h = baseLocalHandle as FileSystemDirectoryHandle & {
          queryPermission?: (opts: { mode: "readwrite" }) => Promise<PermissionState>;
          requestPermission?: (opts: { mode: "readwrite" }) => Promise<PermissionState>;
        };
        if (h.queryPermission && h.requestPermission) {
          const current = await h.queryPermission({ mode: "readwrite" });
          if (current === "granted") {
            localPermissionGranted = true;
          } else {
            const granted = await h.requestPermission({ mode: "readwrite" });
            localPermissionGranted = granted === "granted";
            if (!localPermissionGranted) {
              toast.warning(
                "Local folder access wasn't granted — files will save to your Downloads folder instead.",
                { duration: 7000 },
              );
            }
          }
        }
      } catch (err) {
        console.warn("[FSA] permission request failed:", err);
        localPermissionGranted = false;
      }
    }

    /** Get or create the local FileSystemDirectoryHandle for a given date
     *  folder. Memoizes the in-flight promise so concurrent callers share
     *  one creation attempt.
     *
     *  Provider A-Roll opts out: the user wants files to land directly in
     *  the folder they picked (no PatientName/MM.DD.YY-Topic/ wrapping), so
     *  every "date folder" request resolves to the base handle itself.
     *  Google Drive still nests normally — only the local path is flat. */
    const localSavesFlat = wizardState.subjectKind === "provider";
    const resolveLocalDateFolder = (folderName: string): Promise<FileSystemDirectoryHandle | null> => {
      if (!supportsFsa || !baseLocalHandle || !localPermissionGranted) {
        return Promise.resolve(null);
      }
      if (localSavesFlat) {
        return Promise.resolve(baseLocalHandle);
      }
      const cached = localFolderCache.get(folderName);
      if (cached) return cached;
      const promise = (async () => {
        try {
          let parent: FileSystemDirectoryHandle | null = baseLocalHandle;
          const year = patientYearFolders ? yearFromDateFolder(folderName) : null;
          if (year) {
            let yearPromise = localYearFolderCache.get(year);
            if (!yearPromise) {
              yearPromise = baseLocalHandle
                .getDirectoryHandle(year, { create: true })
                .catch(() => null);
              localYearFolderCache.set(year, yearPromise);
            }
            parent = await yearPromise;
          }
          if (!parent) return null;
          return await parent.getDirectoryHandle(folderName, { create: true });
        } catch {
          return null;
        }
      })();
      localFolderCache.set(folderName, promise);
      return promise;
    };

    /** Get or create the Drive year-wrapper folder ID (e.g. "2026")
     *  directly under the provider folder. Only used by the Provider
     *  A-Roll flow; patients stay flat. Cached per year so all clips
     *  with metadata from the same year share one lookup/creation. */
    const resolveDriveYearFolder = (
      year: string,
      providerFolderId: string,
    ): Promise<string | undefined> => {
      const cached = driveYearFolderCache.get(year);
      if (cached) return cached;
      const promise = (async (): Promise<string | undefined> => {
        try {
          const res = await ensureFolderMutation.mutateAsync({
            name: year,
            parentFolderId: providerFolderId,
          });
          return res.folderId;
        } catch (err) {
          console.warn(`[GDrive] Failed to ensure year folder "${year}":`, err);
          return undefined;
        }
      })();
      driveYearFolderCache.set(year, promise);
      return promise;
    };

    /** Get or create the Drive date folder ID for a given date folder name.
     *  Memoizes the in-flight promise so concurrent callers share one
     *  resolution attempt — without this, parallel uploads to the same
     *  date would all see an empty cache, all call createPatientFolders,
     *  and Drive would create N duplicate folders (it doesn't dedupe by
     *  name). Existing folders are reused via checkDateFolder.
     *
     *  For Provider A-Roll uploads, a year-wrapper folder is auto-
     *  resolved between the provider folder and the date folder, derived
     *  from the date itself (04.08.26 → 2026). The wizard finds or
     *  creates "<provider>/2026/" before placing the date subfolder
     *  inside. New years get auto-created at year boundaries — no
     *  yearly migration work needed. */
    const resolveDriveDateFolder = (folderName: string): Promise<string | undefined> => {
      const cached = driveFolderCache.get(folderName);
      if (cached) return cached;
      const patientFolderId = wizardState.gDriveFolderId;
      if (!patientFolderId) {
        const resolved = Promise.resolve<string | undefined>(undefined);
        driveFolderCache.set(folderName, resolved);
        return resolved;
      }
      const promise = (async (): Promise<string | undefined> => {
        try {
          // Insert the year-wrapper layer between the provider/patient
          // folder and the date folder. Always on for Provider A-Roll;
          // patients get it once the Year-folders switch is on.
          let dateParentId = patientFolderId;
          if (isProvider || patientYearFolders) {
            const year = yearFromDateFolder(folderName);
            if (year) {
              const yearId = await resolveDriveYearFolder(year, patientFolderId);
              if (yearId) dateParentId = yearId;
            }
          }
          // Reuse-if-exists check (under whichever parent we landed on)
          // before creating a fresh folder.
          const dupCheck = await trpcUtils.gdrive.checkDateFolder.fetch({
            patientFolderId: dateParentId,
            dateString: folderName,
          });
          if (dupCheck.exists && dupCheck.existingFolderId) {
            return dupCheck.existingFolderId;
          }
          const result = await createPatientFolders.mutateAsync({
            patientName: wizardState.patientName,
            dateFolder: folderName,
            parentFolderId: dateParentId,
            skipPatientFolder: true,
          });
          return result.dateFolderId;
        } catch (err) {
          console.warn(`[GDrive] Failed to resolve date folder "${folderName}":`, err);
          return undefined;
        }
      })();
      driveFolderCache.set(folderName, promise);
      return promise;
    };

    // ── Drive-direct uploads (3 concurrent, capped to avoid OAuth token races) ─
    // Each file:  local save (optional) → DB row → Drive resumable upload →
    // finalize labels. The Manus Forge S3 hop has been removed entirely.
    const uploadTasks = files
      .map((entry, i) => ({ entry, i }))
      .filter(({ entry }) => entry.phase === "tagging" || entry.phase === "error");

    let driveFailureCount = 0;
    // In the auto-detect flow, gDriveDateFolderId starts undefined and gets
    // populated per date group inside the loop. We only need the patient
    // folder ID to be ready.
    const wantsDrive =
      wizardState.destinations.includes("google_drive") &&
      !!(wizardState.gDriveDateFolderId || wizardState.gDriveFolderId);

    const DRIVE_CONCURRENCY = 3;
    let activeSlots = 0;
    const waitForSlot = () =>
      new Promise<void>((resolve) => {
        const check = () => {
          if (activeSlots < DRIVE_CONCURRENCY) { activeSlots++; resolve(); }
          else setTimeout(check, 50);
        };
        check();
      });

    await Promise.all(
      uploadTasks.map(async ({ entry, i }) => {
        await waitForSlot();
        try {
          let renamedName =
            finalNameByIndex.get(i) || entry.renamedName || buildRenamedName(entry.originalName, entry.activities);

          // Content hash (SHA-256), started now and saved on the DB row the
          // moment it's created — right after the local copy is written, so
          // well inside the NAS scanner's settle window. The scanner then
          // recognises the clip by hash instead of ingesting the NAS copy as
          // a second, untagged row (which double-counted reports). Reused
          // for finalize-analysis below.
          const hashPromise = sha256HexOfFile(entry.file);

          // ── Resolve this file's MM.DD.YY folder for both destinations ───
          const fileDateFolder = folderForEntry(entry);
          const fileLocalHandle = await resolveLocalDateFolder(fileDateFolder);
          const fileDriveFolderId = wizardState.destinations.includes("google_drive")
            ? await resolveDriveDateFolder(fileDateFolder)
            : undefined;
          if (fileDriveFolderId) {
            updateFile(i, { resolvedDriveFolderId: fileDriveFolderId });
          }

          // Hash before the local write starts, so the row can carry it as
          // soon as the file lands on the NAS.
          const contentHash = await hashPromise;

          // ── Step 1: Local save (independent of Drive upload) ────────────────
          let localSaveSucceeded = false;
          if (wizardState.destinations.includes("local")) {
            let fsaWriteSucceeded = false;
            if (fileLocalHandle) {
              try {
                // Never overwrite a DIFFERENT clip already in the folder (e.g.
                // one from an earlier batch the same day with the same short
                // number) — take the next free name, and use it for Drive + the
                // DB too so every copy matches. Same name + same size is this
                // clip from a retried upload, so that one is just rewritten.
                const existing = new Map<string, string>();
                for await (const n of (fileLocalHandle as unknown as { keys(): AsyncIterable<string> }).keys()) {
                  existing.set(n.toLowerCase(), n);
                }
                const clash = existing.get(renamedName.toLowerCase());
                if (clash) {
                  const onDisk = await (await fileLocalHandle.getFileHandle(clash)).getFile().catch(() => null);
                  if (!onDisk || onDisk.size !== entry.file.size) {
                    renamedName = withCopyNumber(renamedName, (n) => existing.has(n.toLowerCase()));
                    updateFile(i, { renamedName });
                  }
                }
                const fileHandle = await fileLocalHandle.getFileHandle(renamedName, { create: true });
                const writable = await fileHandle.createWritable();
                await writable.write(entry.file);
                await writable.close();
                fsaWriteSucceeded = true;
                localSaveSucceeded = true;
              } catch {
                /* fall through to browser download */
              }
            }
            if (!fsaWriteSucceeded) {
              triggerDownload(entry.file, renamedName);
              localSaveSucceeded = true;
            }
          }

          // ── Step 2: Insert DB row ─────────────────────────────────────────
          let fileId = entry.id;
          if (!fileId) {
            const created = await createFootage.mutateAsync({
              sessionId: wizardState.sessionId!,
              driveFolderId: wizardState.driveFolderId ?? "",
              originalName: entry.originalName,
              mimeType: entry.file.type || "application/octet-stream",
              // Stamps subject_kind on the row so Footage Search can filter
              // A-roll vs B-roll. Server looks up patient_color_label via
              // Drive (cached per folder).
              subjectKind: isProvider ? "provider" : "patient",
              // Audio routes through Whisper-only on the server (Pegasus
              // is video-only); the Description sidecar is generated by
              // Claude-summarizing the transcript instead.
              mediaKind: isAudioFile(entry.file) ? "audio" : "video",
              // Associated provider name(s) chosen on the upload screen
              // (patient workflow only; undefined for provider A-Roll).
              providers: isProvider || batchProviders.length === 0 ? undefined : batchProviders,
              // Who filmed this batch (chosen on the upload screen; defaults
              // to the logged-in user). Stamped on every row for the "Filmed
              // by" search filter.
              filmedByUserId: wizardState.filmedByUserId,
              filmedByName: wizardState.filmedByName,
              // A-Roll umbrella topic — same value that shapes the dated Drive
              // folder ("MM.DD.YY - {topic}"). Persisted so Search can find +
              // filter by topic. Only meaningful for provider A-Roll.
              videoTopic: isProvider ? resolvedTopicRef.current?.trim() || undefined : undefined,
              // This clip's MM.DD.YY session folder (named from its own
              // recording date) — saved as the filming date reports use.
              dateFolder: fileDateFolder,
              contentHash: contentHash ?? undefined,
            });
            fileId = created.id;
            updateFile(i, { id: fileId, error: undefined });
          }

          // ── Step 3: Drive upload (the only remote upload step) ────────────
          let driveFileId: string | undefined;
          if (wantsDrive && fileDriveFolderId) {
            const driveStart = Date.now();
            let driveSpeedSamples: Array<[number, number]> = [];
            updateFile(i, {
              phase: "uploading",
              drivePhase: "uploading",
              progress: 0,
              driveProgress: 0,
              driveUploadStartTime: driveStart,
              driveSpeedSamples: [],
              driveSpeedBps: 0,
              driveEtaSec: undefined,
              uploadStartTime: driveStart,
              uploadBytesLoaded: 0,
              uploadTotalBytes: entry.file.size,
            });

            const MAX_FILE_RETRIES = 3;
            let lastErr: Error | null = null;
            for (let attempt = 1; attempt <= MAX_FILE_RETRIES; attempt++) {
              try {
                driveFileId = await uploadFileToDriveDirectly(
                  entry.file,
                  renamedName,
                  fileDriveFolderId,
                  (pct, loaded, total) => {
                    const now = Date.now();
                    driveSpeedSamples = ([...driveSpeedSamples, [now, loaded] as [number, number]]).slice(-5);
                    let speedBps = 0;
                    let etaSec: number | undefined;
                    if (driveSpeedSamples.length >= 2) {
                      const [t0, b0] = driveSpeedSamples[0];
                      const [t1, b1] = driveSpeedSamples[driveSpeedSamples.length - 1];
                      const dt = (t1 - t0) / 1000;
                      const db = b1 - b0;
                      speedBps = dt > 0 ? db / dt : 0;
                      if (speedBps > 0 && total > 0) etaSec = (total - loaded) / speedBps;
                    }
                    updateFile(i, {
                      progress: pct,
                      driveProgress: pct,
                      driveBytesLoaded: loaded,
                      driveTotalBytes: total,
                      uploadBytesLoaded: loaded,
                      uploadTotalBytes: total,
                      driveSpeedBps: speedBps,
                      driveEtaSec: etaSec,
                      speedBps,
                      etaSec,
                      driveSpeedSamples,
                    });
                  },
                );
                lastErr = null;
                break;
              } catch (err: any) {
                lastErr = err;
                const retryable = /503|502|429|network|timeout|ECONNRESET|ETIMEDOUT|aborted|401/i.test(err?.message ?? "");
                if (attempt < MAX_FILE_RETRIES && retryable) {
                  const backoffMs = attempt * 4000 + Math.random() * 2000;
                  updateFile(i, { error: `Drive busy — retrying in ${Math.round(backoffMs / 1000)}s…` });
                  await new Promise((r) => setTimeout(r, backoffMs));
                } else {
                  break;
                }
              }
            }

            if (lastErr) {
              const localNote = localSaveSucceeded ? " (saved locally ✔)" : "";
              updateFile(i, { phase: "error", drivePhase: "error", error: lastErr.message + localNote, errorReason: lastErr.message + localNote, progress: 0 });
              toast.error(`Upload failed for "${entry.originalName}": ${lastErr.message}${localNote}`, { duration: 8000 });
              driveFailureCount++;
              return;
            }
            updateFile(i, { drivePhase: "done", driveProgress: 100, driveFileId, driveSpeedBps: 0, driveEtaSec: undefined });
          } else if (wizardState.destinations.includes("google_drive") && !fileDriveFolderId) {
            toast.info(`Google Drive: "${renamedName}" skipped — no Drive folder available for ${fileDateFolder}`, { duration: 3000 });
          }

          // ── Step 3.5: Bedrock transcription + summary ───────────────────────
          // Runs AFTER the video is safely in Drive (and on disk locally),
          // so a transcription failure never costs the user the actual footage.
          // Failure is non-fatal — we toast a warning and move on.
          //
          // Triggers:
          //   - Always for provider A-Roll uploads.
          //   - Always for patient clips when the Exam checkbox was ticked
          //     on the initial workflow page (wizardState.isExam) — exam
          //     sessions land in a "MM.DD.YY - exam" folder and we want
          //     every clip transcribed regardless of activity tags.
          //   - For other patient clips, only when one of their activity
          //     tags is in TRANSCRIBE_TRIGGER_ACTIVITY_TAGS (currently
          //     "Explanation" or "Discussion"). The trigger list lives in
          //     src/shared/const.ts.
          // Raw transcript captured here (when available) so we can pass it
          // to /api/finalize-analysis below without re-running Whisper on
          // the server. A-roll always runs Whisper above; for B-roll it
          // depends on `shouldTranscribe`.
          let entryRawTranscript: string | undefined;
          let entryRawTranscriptSrt: string | undefined;
          const shouldTranscribe = isProvider || wizardState.isExam || activitiesTriggerTranscription(entry.activities);
          if (shouldTranscribe && entry.file.type.startsWith("video/")) {
            updateFile(i, { phase: "transcribing", progress: 0 });
            try {
              // Funnel through runTranscribeSerially — see the helper for why.
              // While this clip is waiting its turn the card shows "Transcribing…"
              // even though the POST hasn't started; that's acceptable as a
              // simple status, and it prevents the proxy 502s we saw when
              // several Explanation/Discussion clips fired in parallel.
              const result = await runTranscribeSerially(() =>
                transcribeProviderVideo(entry.file, (phase, pct) => {
                  updateFile(i, { transcribePhase: phase, progress: pct });
                }),
              );
              if (result?.transcript) entryRawTranscript = result.transcript;
              if (result?.srtContent) entryRawTranscriptSrt = result.srtContent;
              if (result) {
                // Stash the transcript on the FileEntry so the review dialog
                // and any later "regenerate suggestion" UI can reach it.
                if (isProvider && result.txtContent) {
                  updateFile(i, { transcript: result.txtContent });
                }
                // Sidecar files (.txt / .srt) are NOT written to Drive or
                // local at upload time. The plain text + SRT both get
                // passed to /api/finalize-analysis below; the DB is the
                // source of truth. Bundle downloads in Footage Search
                // materialize sidecar files on demand from DB content.
                //
                // Queue an AI Topic Tag suggestion for clips the user didn't
                // tag manually. Sidecar Drive IDs are null since we don't
                // write sidecars anymore — the rename flow already handles
                // that (no-ops gracefully when IDs are null).
                if (isProvider && result.txtContent && entry.activities.length === 0 && fileId != null) {
                  tagSuggestionCandidates.push({
                    entryIndex: i,
                    transcript: result.txtContent,
                    driveFileId: driveFileId ?? null,
                    txtSidecarDriveFileId: null,
                    srtSidecarDriveFileId: null,
                    dateFolder: fileDateFolder,
                    originalName: entry.originalName,
                    currentRenamedName: renamedName,
                    dbFileId: fileId,
                    thumbnail: entry.thumbnail,
                  });
                }
              }
            } catch (err: any) {
              console.warn("[transcribe] Transcription failed:", err?.message ?? err);
              toast.warning(`Transcription skipped for "${entry.originalName}": ${err?.message ?? "unknown error"}`, { duration: 7000 });
            }
          }

          // ── Step 4: Finalize labels in DB ────────────────────────────────
          // Pass driveFileId through so the row gets linked back to its
          // Drive video — without this the row stays searchable (Pegasus
          // already wrote ai_description via /api/finalize-analysis) but
          // Footage Search can't render the inline preview and Bundle
          // Download can't stream the bytes. Undefined for local-only
          // uploads, which is fine.
          updateFile(i, { phase: "uploading_final", progress: 95 });
          await finalizeFootage.mutateAsync({
            fileId: fileId!,
            activities: entry.activities,
            renamedName,
            driveFolderId: wizardState.driveFolderId ?? undefined,
            driveFileId,
            tagKind: isProvider ? "topic" : "activity",
            providers: effectiveProvidersFor(entry),
          });

          // ── Step 4.5: Pegasus description + embedding (fire-and-forget) ──
          // Server downloads the just-uploaded video back from Drive, runs
          // Pegasus, writes a .description.txt sidecar, computes a Titan
          // embedding, and updates the row so it shows up in /footage-search
          // search. Failure is logged server-side and does not block
          // confirmation — the backfill script can pick the row up later.
          //
          // Predict eligibility first so we can skip the POST (and surface a
          // summary toast) when Pegasus would reject — image files, clips
          // outside the duration window, or files over 2 GB.
          const eligibility = await predictPegasusEligibility(entry.file);
          if (!eligibility.eligible) {
            pegasusSkips.push({ name: renamedName, reason: eligibility.reason });
          }
          let pegasusQueued = false;
          if (eligibility.eligible && fileId != null) {
            // Two paths into Pegasus:
            //   (a) Drive-backed upload — server downloads from Drive and
            //       runs Pegasus. Cheaper since the bytes already crossed
            //       the wire once.
            //   (b) Local-only upload (no Drive selected) — client streams
            //       the video to /api/finalize-analysis-chunk in 5 MB
            //       chunks, then hits /api/finalize-analysis-local to kick
            //       off Pegasus on the staged tmp file. Bandwidth-heavier
            //       but the only way to get a description without Drive.
            // Both paths persist the description to the DB and the existing
            // UploadContext poller mirrors it into the local Ds+Ts/ folder.
            const wantsDriveAnalysis = !!(driveFileId && fileDriveFolderId);
            if (wantsDriveAnalysis) {
              // contentHash was computed before the local write (see the
              // top of this task) and is already on the row; it's re-sent
              // here for rows created by an earlier attempt.
              void (async () => {
                try {
                  await fetch("/api/finalize-analysis", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      footageFileId: fileId,
                      driveFileId,
                      driveFolderId: fileDriveFolderId,
                      // A-roll already ran Whisper above; pass both the
                      // plain text + SRT so the server doesn't repeat it
                      // AND so transcript_srt lands in the DB. B-roll
                      // usually hasn't transcribed, so server's allowed to
                      // run Whisper itself.
                      skipTranscript: isProvider,
                      transcriptText: entryRawTranscript,
                      transcriptSrt: entryRawTranscriptSrt,
                      // Dedup key. Server persists this on the row so the
                      // NAS scanner can later look up this same clip and
                      // skip re-analyzing it when it discovers the local
                      // NAS copy this upload wrote out.
                      contentHash: contentHash ?? undefined,
                    }),
                  });
                } catch (err) {
                  console.warn("[finalize-analysis] fire-and-forget failed:", err);
                }
              })();
            } else if (wizardState.destinations.includes("local")) {
              // Local-only: stream bytes to server, kick off Pegasus there.
              // Fire-and-forget (no await) so it doesn't block batch
              // progress. Errors are logged inside the helper.
              const fileIdForUpload = fileId;
              void uploadVideoForLocalPegasus(entry.file, fileIdForUpload).catch((err) => {
                console.warn("[local-pegasus] fire-and-forget errored:", err);
              });
            }

            // Always enqueue the Pegasus poll — its job now is purely
            // failure-toast detection. Sidecars are never written at
            // upload time (DB is the source of truth; bundle downloads
            // materialize them on demand), so both sidecar fields stay
            // null. The poller surfaces a warning toast if Pegasus
            // fails server-side (throttle, S3 error, transcoding fail,
            // etc.) so the user knows what missed analysis.
            //
            // onStatusChange flips the card's `pegasusPhase` to "done" /
            // "failed" so the "Analyzing…" sub-state on the upload card
            // resolves to a green AI badge (or red warning) without the
            // user having to refresh.
            const fileIndexForPoll = i;
            enqueuePegasusPoll({
              footageFileId: fileId,
              displayName: renamedName,
              sidecarName: null,
              dateFolderHandle: null,
              onStatusChange: (status) => {
                updateFile(fileIndexForPoll, { pegasusPhase: status });
              },
            });
            pegasusQueued = true;
          }

          updateFile(i, {
            phase: "done",
            progress: 100,
            // Only show the "Analyzing…" indicator when there's actually a
            // Pegasus job in flight to wait for — skipped clips (e.g.
            // images, too-short, too-long) would otherwise sit "pending"
            // forever and look broken.
            ...(pegasusQueued ? { pegasusPhase: "pending" as const } : {}),
          });

          processedFiles.push({
            id: fileId!,
            originalName: entry.originalName,
            renamedName,
            activities: entry.activities,
            driveFileId,
            status: "uploaded",
            thumbnail: entry.thumbnail,
            thumbnailWidth: entry.thumbnailWidth,
            thumbnailHeight: entry.thumbnailHeight,
            dateFolder: fileDateFolder,
          });
        } finally {
          activeSlots--;
        }
      }),
    );

    // Add skipped files to processedFiles so they appear on the confirmation screen.
    files.forEach((entry) => {
      if (entry.phase === "skipped") {
        processedFiles.push({
          id: entry.id ?? -1,
          originalName: entry.originalName,
          renamedName: entry.renamedName || entry.originalName,
          activities: entry.activities,
          driveFileId: entry.driveFileId,
          status: "skipped",
          thumbnail: entry.thumbnail,
          thumbnailWidth: entry.thumbnailWidth,
          thumbnailHeight: entry.thumbnailHeight,
          dateFolder: entry.resolvedDateFolder,
        });
      }
    });

    // Skipped files are counted separately — they don't block navigation.
    const skippedCount = files.filter((f) => f.phase === "skipped").length;
    // s3FailureCount kept for compatibility with the dialog logic below — Drive
    // failures are the only remote-upload failures now.
    const s3FailureCount = 0;

    // Surface a single summary toast for any files that won't get a
    // Pegasus description. Fires regardless of which branch (success or
    // proceed-anyway dialog) we take below — and stays on-screen long
    // enough to read.
    if (pegasusSkips.length > 0) {
      const grouped = new Map<string, string[]>();
      for (const skip of pegasusSkips) {
        const arr = grouped.get(skip.reason) ?? [];
        arr.push(skip.name);
        grouped.set(skip.reason, arr);
      }
      const lines = Array.from(grouped.entries()).map(
        ([reason, names]) => `${names.length} ${reason}`,
      );
      toast.warning(
        `AI description skipped for ${pegasusSkips.length} file(s): ${lines.join(", ")}.`,
        { duration: 10000 },
      );
    }

    if (processedFiles.length > 0) {
      const hasDriveFailures = driveFailureCount > 0;
      const hasS3Failures = s3FailureCount > 0;
      const hasSkipped = skippedCount > 0;
      if (hasS3Failures || hasDriveFailures || hasSkipped) {
        // Store processed files so the proceed-anyway dialog can navigate when user confirms
        processedFilesRef.current = processedFiles;
        setAppPhase("adding");
        setUploadActive(false);
        // Slight delay so React can flush the state before we show the dialog
        setTimeout(() => setProceedAnywayOpen(true), 400);
      } else {
        // Happy path. Provider A-Roll's per-file Topic Tag suggestion runs
        // here before the success toast: clips uploaded without a tag get a
        // Bedrock-proposed tag, all suggestions surface in one review
        // dialog, and accepted rows have their Drive + local files renamed
        // (and their DB rows updated) before we navigate to Confirmation.
        if (isProvider && tagSuggestionCandidates.length > 0) {
          await runPerFileTagSuggestionFlow(tagSuggestionCandidates, processedFiles, baseLocalHandle, localSavesFlat);
        }
        setUploadActive(false);
        toast.success(`${processedFiles.length} file(s) uploaded successfully!`);
        setTimeout(() => onComplete(processedFiles, resolvedTopicRef.current), 800);
      }
    } else {
      setAppPhase("adding");
      setUploadActive(false);
    }
  };

  /**
   * Provider A-Roll only. For each clip that finished uploading without a
   * Topic Tag, ask Bedrock for a suggestion from the clip's own transcript,
   * surface every suggestion in a single review dialog, and (on accept)
   * apply the chosen tag by:
   *   1. Renaming the Drive file (drive.files.update — file ID stays stable),
   *   2. Renaming the local file via FileSystemHandle.move() where supported,
   *   3. Re-calling footage.finalize so the DB row picks up the new tag +
   *      renamedName.
   * Every step is best-effort; partial failures fall back to "this file
   * keeps its untagged name" rather than blocking the success-page nav.
   */
  const runPerFileTagSuggestionFlow = async (
    candidates: Array<{
      entryIndex: number;
      transcript: string;
      driveFileId: string | null;
      txtSidecarDriveFileId: string | null;
      srtSidecarDriveFileId: string | null;
      dateFolder: string;
      originalName: string;
      currentRenamedName: string;
      dbFileId: number;
      thumbnail?: string;
    }>,
    processedFiles: ProcessedFile[],
    baseLocalHandle: FileSystemDirectoryHandle | null | undefined,
    localSavesFlat: boolean,
  ): Promise<void> => {
    // Open the dialog immediately with a spinner so the user knows something
    // is happening while Bedrock chews on transcripts.
    setTagReviewDialog({ open: true, rows: [], resolve: null, fetching: true });

    // Fetch suggestions in parallel — one Bedrock call per clip. Failures
    // for an individual clip drop that row from the dialog (no point
    // surfacing an empty input).
    const suggestionResults = await Promise.all(
      candidates.map(async (c) => {
        try {
          const { tag } = await suggestTopicTag.mutateAsync({ transcript: c.transcript });
          return { candidate: c, tag };
        } catch (err: any) {
          console.warn(
            `[tag-suggest] Bedrock call failed for entry ${c.entryIndex}:`,
            err?.message ?? err,
          );
          return { candidate: c, tag: null as string | null };
        }
      }),
    );
    const rows: TagSuggestionRow[] = suggestionResults
      .filter((r): r is { candidate: typeof candidates[number]; tag: string } => !!r.tag)
      .map(({ candidate, tag }) => ({
        entryIndex: candidate.entryIndex,
        driveFileId: candidate.driveFileId,
        txtSidecarDriveFileId: candidate.txtSidecarDriveFileId,
        srtSidecarDriveFileId: candidate.srtSidecarDriveFileId,
        dateFolder: candidate.dateFolder,
        originalName: candidate.originalName,
        currentRenamedName: candidate.currentRenamedName,
        dbFileId: candidate.dbFileId,
        thumbnail: candidate.thumbnail,
        suggested: tag,
        edited: tag,
        skipped: false,
      }));

    if (rows.length === 0) {
      // No usable suggestions — close the dialog silently and continue to nav.
      setTagReviewDialog({ open: false, rows: [], resolve: null, fetching: false });
      return;
    }

    // Hand the rows off to the dialog and wait for the user's decision.
    const accepted = await new Promise<TagSuggestionRow[] | null>((resolve) => {
      setTagReviewDialog({ open: true, rows, resolve, fetching: false });
    });
    if (!accepted || accepted.length === 0) return;

    setApplyingTagSuggestions(true);
    try {
      // Unique original-filenames that had at least one Drive rename
      // failure (video, .txt, or .srt). Used to size the post-apply
      // warning toast without triple-counting per-file failures.
      const filesWithDriveFailure = new Set<string>();
      await Promise.all(
        accepted.map(async (row) => {
          if (row.skipped) return;
          const cleanedTag = row.edited.replace(/[<>:"/\\|?*]/g, "_").trim();
          if (!cleanedTag) return;
          // Keep the clip's number as finalized (it may have been widened to
          // avoid a name clash) and only swap the tag.
          const newRenamedName = replaceActivities(row.currentRenamedName, [cleanedTag]);
          // Sidecar names move with the video — sidecars share the
          // video's base name, just with .txt / .srt extensions.
          const oldTxtName = withExtension(row.currentRenamedName, "txt");
          const oldSrtName = withExtension(row.currentRenamedName, "srt");
          const newTxtName = withExtension(newRenamedName, "txt");
          const newSrtName = withExtension(newRenamedName, "srt");

          // 1. Drive rename — video first, then each sidecar that actually
          //    landed on Drive. A failure on any of the three is logged and
          //    counted but doesn't stop the others.
          const renameOnDrive = async (fileId: string | null, newName: string, label: string) => {
            if (!fileId) return;
            try {
              await renameDriveFileMutation.mutateAsync({ fileId, newName });
            } catch (err: any) {
              console.warn(
                `[tag-suggest] Drive rename failed for ${label} (${row.originalName}):`,
                err?.message ?? err,
              );
              filesWithDriveFailure.add(row.originalName);
            }
          };
          await renameOnDrive(row.driveFileId, newRenamedName, "video");
          await renameOnDrive(row.txtSidecarDriveFileId, newTxtName, ".txt");
          await renameOnDrive(row.srtSidecarDriveFileId, newSrtName, ".srt");

          // 2. Local rename via FSA — same three files (video + sidecars),
          //    all sitting in the same parent directory. Provider flow saves
          //    flat into the user-picked folder; other flows nest under
          //    MM.DD.YY/, so descend into that subfolder first.
          if (baseLocalHandle) {
            let parentHandle: FileSystemDirectoryHandle | null = null;
            try {
              parentHandle = localSavesFlat
                ? baseLocalHandle
                : await baseLocalHandle.getDirectoryHandle(row.dateFolder, { create: false });
            } catch (err: any) {
              console.warn(
                `[tag-suggest] Could not open local date folder ${row.dateFolder}:`,
                err?.message ?? err,
              );
            }
            if (parentHandle) {
              const renameLocally = async (
                handle: FileSystemDirectoryHandle,
                oldName: string,
                newName: string,
                label: string,
              ) => {
                try {
                  await renameLocalFile(handle, oldName, newName);
                } catch (err: any) {
                  // Local-rename failures are quiet — the file is still on
                  // disk, just with its original name. Sidecars commonly
                  // don't exist when transcription failed or local-saves
                  // were skipped; either way, no toast.
                  console.warn(
                    `[tag-suggest] Local rename failed for ${label} (${row.originalName}):`,
                    err?.message ?? err,
                  );
                }
              };
              await renameLocally(parentHandle, row.currentRenamedName, newRenamedName, "video");
              // Sidecars live in a "sidecars" subfolder (was "Ds+Ts"
              // before the rename). Open it no-create for renames; if
              // neither name is present this is a pre-rollout session
              // with nothing to rename, so we skip quietly. Try the
              // new name first, fall back to the legacy name.
              let sidecarHandle: FileSystemDirectoryHandle | null = null;
              try {
                sidecarHandle = await parentHandle.getDirectoryHandle("sidecars", { create: false });
              } catch {
                try {
                  sidecarHandle = await parentHandle.getDirectoryHandle("Ds+Ts", { create: false });
                } catch {
                  /* no sidecar subfolder under either name — skip renames */
                }
              }
              if (sidecarHandle) {
                await renameLocally(sidecarHandle, oldTxtName, newTxtName, ".txt");
                await renameLocally(sidecarHandle, oldSrtName, newSrtName, ".srt");
              }
            }
          }
          // 3. Update the DB row with the new tag + renamedName.
          try {
            await finalizeFootage.mutateAsync({
              fileId: row.dbFileId,
              activities: [cleanedTag],
              renamedName: newRenamedName,
              driveFolderId: wizardState.driveFolderId ?? undefined,
              tagKind: "topic",
            });
          } catch (err: any) {
            console.warn(
              `[tag-suggest] DB finalize failed for ${row.originalName}:`,
              err?.message ?? err,
            );
          }
          // 4. Update the in-memory FileEntry + matching ProcessedFile so the
          //    file card and the success page reflect the new name.
          updateFile(row.entryIndex, {
            activities: [cleanedTag],
            renamedName: newRenamedName,
          });
          const pf = processedFiles.find((f) => f.id === row.dbFileId);
          if (pf) {
            pf.activities = [cleanedTag];
            pf.renamedName = newRenamedName;
          }
        }),
      );
      if (filesWithDriveFailure.size > 0) {
        const appliedCount =
          accepted.filter((r) => !r.skipped).length - filesWithDriveFailure.size;
        toast.warning(
          `Topic tags applied to ${appliedCount} file(s); Drive rename failed for ${filesWithDriveFailure.size}. Local saves and DB rows still got the new names.`,
          { duration: 8000 },
        );
      }
    } finally {
      setApplyingTagSuggestions(false);
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  // Count tagging-phase files (ready to upload) and done/error/skipped
  const taggingCount = files.filter((f) => f.phase === "tagging" || f.phase === "error").length;
  const doneCount = files.filter((f) => f.phase === "done" || f.phase === "skipped").length;
  const errorCount = files.filter((f) => f.phase === "error").length;
  const skippedCount = files.filter((f) => f.phase === "skipped").length;

  // ── Session date display ─────────────────────────────────────────────────
  // In auto-detect mode (empty wizardState.dateFolder) the banner shows the
  // unique dates detected so far from files. While detection is still
  // pending for some files, those are excluded — the chip updates as they
  // resolve. Falls back to "Auto-detected from footage" when nothing has
  // come back yet.
  const examSuffix = buildDateFolderSuffix(wizardState);
  const detectedDateLabels: string[] = (() => {
    if (wizardState.dateFolder && wizardState.dateFolder.length > 0) {
      return [wizardState.dateFolder];
    }
    const set = new Set<string>();
    for (const f of files) {
      const d = f.overrideDate || f.detectedDate;
      if (d) set.add(`${d}${examSuffix}`);
    }
    return Array.from(set).sort();
  })();
  const sessionDateDisplay =
    detectedDateLabels.length === 0
      ? "Auto-detected from footage"
      : detectedDateLabels.length === 1
        ? detectedDateLabels[0]
        : `${detectedDateLabels.length} dates · ${detectedDateLabels.join(", ")}`;
  // For the folder breadcrumb: show first date if single, "…" if multiple
  const sessionFolderBreadcrumb =
    detectedDateLabels.length === 1 ? detectedDateLabels[0] : detectedDateLabels.length > 1 ? "…" : "…";
  // Pending detection count — files added but no detectedDate yet
  const pendingDetectionCount = wizardState.dateFolder
    ? 0
    : files.filter((f) => !f.detectedDate && !f.overrideDate).length;
  const fallbackCount = files.filter((f) => f.detectedDateSource === "lastModified").length;

  // ── Upload queue indicator ────────────────────────────────────────────────
  // Shown in the session banner during the uploading phase.
  const totalQueued = files.length;

  // Provider summary for the session banner (patient flow). Batch providers
  // apply to every clip and are editable here; per-clip additions are
  // aggregated separately and flagged with the clip count. (batchProviders is
  // component state, declared up top so the create/finalize calls read it.)
  const clipProviderCounts = (() => {
    if (isProvider) return [] as Array<{ name: string; count: number }>;
    const batchLower = new Set(batchProviders.map((p) => p.toLowerCase()));
    const counts = new Map<string, number>();
    for (const f of files) {
      for (const p of f.providers ?? []) {
        if (batchLower.has(p.toLowerCase())) continue;
        counts.set(p, (counts.get(p) ?? 0) + 1);
      }
    }
    return [...counts.entries()].map(([name, count]) => ({ name, count }));
  })();
  // Show whenever there's something to display, or during the "adding" phase
  // (so the user can add/edit batch providers before uploading).
  const showProviderSummary =
    !isProvider &&
    (appPhase === "adding" || batchProviders.length > 0 || clipProviderCounts.length > 0);
  // ─ S3 phase ─
  const activelyUploading = files.filter((f) => f.phase === "uploading" || f.phase === "uploading_final").length;
  const uploadFailedCount = files.filter((f) => f.phase === "error" && appPhase === "uploading").length;
  // Average S3 progress across all files (0–100)
  const overallUploadPct = totalQueued > 0
    ? Math.round(files.reduce((sum, f) => sum + (f.progress ?? 0), 0) / totalQueued)
    : 0;
  // ─ Drive phase ─
  // Only meaningful when Drive is a selected destination
  const driveIsDestination = wizardState.destinations.includes("google_drive");
  // Drive phase has "started" once at least one file has a non-undefined drivePhase
  const drivePhaseStarted = driveIsDestination && files.some((f) => f.drivePhase !== undefined);
  const driveDoneCount = files.filter((f) => f.drivePhase === "done").length;
  const driveActiveCount = files.filter((f) => f.drivePhase === "uploading").length;
  const driveFailedCount = files.filter((f) => f.drivePhase === "error").length;
  // Files that have a drivePhase at all (i.e. were included in the Drive loop)
  const driveTotalCount = files.filter((f) => f.drivePhase !== undefined).length;

  return (
    <div className="space-y-6">
      {/* Session Banner */}
      <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-xl border border-primary/20">
        <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-semibold text-primary-foreground">
            {wizardState.patientName.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">{wizardState.patientName}</div>
          <div className="text-xs text-muted-foreground">
            Session: <span className="font-medium">{sessionDateDisplay}</span>
            {pendingDetectionCount > 0 && (
              <span className="ml-1 text-muted-foreground/70">· detecting {pendingDetectionCount}…</span>
            )}
            {fallbackCount > 0 && (
              <span
                className="ml-1 text-amber-600"
                title={`${fallbackCount} file(s) had no embedded creation date — using OS modified date as a best guess.`}
              >
                · ⚠ {fallbackCount} using file date
              </span>
            )}
            {wizardState.destinations.length > 0 && (
              <span className="ml-2">
                · {wizardState.destinations.map((d) => (d === "local" ? "Local Drive" : "Google Drive")).join(" + ")}
              </span>
            )}
            {(wizardState.localFolderPath || wizardState.patientFolderHandle) && (
              <span className="ml-2 text-primary font-medium">
                📁 {wizardState.localFolderPath ? `${wizardState.localFolderPath}/` : ""}{wizardState.patientName}/{sessionFolderBreadcrumb}/
              </span>
            )}
          </div>
          {/* Provider summary — batch providers apply to every clip; per-clip
              additions are listed separately, flagged with their clip count. */}
          {showProviderSummary && (() => {
            // Batch providers are editable only before the upload starts.
            const editing = editingBatchProviders && appPhase === "adding";
            const clipChips = clipProviderCounts.map(({ name, count }) => (
              <span
                key={`clip-${name}`}
                className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-1.5 py-0.5 text-muted-foreground"
                title={`Added on ${count} clip${count === 1 ? "" : "s"} only — not applied to the whole batch`}
              >
                {name}
                <span className="text-[10px] text-muted-foreground/70">
                  · on {count} clip{count === 1 ? "" : "s"}
                </span>
              </span>
            ));
            return (
              <div className="mt-1.5 text-xs">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Users className="w-3 h-3 text-muted-foreground" />
                  <span className="text-muted-foreground font-medium">Providers:</span>
                  {!editing && (
                    <>
                      {batchProviders.length === 0 && clipProviderCounts.length === 0 && (
                        <span className="text-muted-foreground/70 italic">none yet</span>
                      )}
                      {batchProviders.map((name) => (
                        <span
                          key={`batch-${name}`}
                          className="inline-flex items-center rounded-md bg-muted border border-border px-1.5 py-0.5 font-medium text-foreground/80"
                        >
                          {name}
                        </span>
                      ))}
                      {clipChips}
                      {appPhase === "adding" && (
                        <button
                          type="button"
                          onClick={() => setEditingBatchProviders(true)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                          title="Add or edit the providers applied to every clip in this batch"
                        >
                          <Pencil className="w-3 h-3" />
                          {batchProviders.length > 0 ? "Edit" : "Add"}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {editing && (
                  <div className="mt-1.5 space-y-1.5">
                    <ProviderPicker
                      value={batchProviders}
                      onChange={setBatchProviders}
                      compact
                      placeholder="Add a provider for the whole batch…"
                    />
                    {clipProviderCounts.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wide">
                          Clip-specific:
                        </span>
                        {clipChips}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditingBatchProviders(false)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                    >
                      <Check className="w-3 h-3" />
                      Done
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-1.5">
          {/* Two-phase upload queue indicator — only visible during the uploading phase */}
          {appPhase === "uploading" && totalQueued > 0 && (
            <div className="flex flex-col gap-1 items-end">
              {/* S3 / local phase pill */}
              <div className={cn(
                "flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                uploadFailedCount > 0
                  ? "bg-red-50 border-red-200 text-red-700"
                  : doneCount === totalQueued
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-primary/10 border-primary/20 text-primary"
              )}>
                {doneCount === totalQueued ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <Loader2 className="w-3 h-3 animate-spin" />
                )}
                <span>
                  {doneCount === totalQueued
                    ? `Upload · ${totalQueued} done`
                    : uploadFailedCount > 0
                      ? `Upload · ${doneCount}/${totalQueued} · ${uploadFailedCount} failed`
                      : activelyUploading > 0
                        ? `Upload · ${doneCount}/${totalQueued} · ${activelyUploading} active`
                        : `Upload · ${doneCount}/${totalQueued}`
                  }
                </span>
              </div>
              {/* Drive phase pill — only when Drive is a destination and the phase has started */}
              {driveIsDestination && drivePhaseStarted && (
                <div className={cn(
                  "flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                  driveFailedCount > 0
                    ? "bg-red-50 border-red-200 text-red-700"
                    : driveTotalCount > 0 && driveDoneCount === driveTotalCount
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-[#4986e7]/10 border-[#4986e7]/30 text-[#4986e7]"
                )}>
                  {/* Status icon */}
                  {driveTotalCount > 0 && driveDoneCount === driveTotalCount ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : isRetryingAll ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : driveFailedCount > 0 ? (
                    <AlertCircle className="w-3 h-3" />
                  ) : (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  )}
                  <Cloud className="w-3 h-3" />
                  <span>
                    {driveTotalCount > 0 && driveDoneCount === driveTotalCount
                      ? `Drive · ${driveTotalCount} done`
                      : driveFailedCount > 0
                        ? `Drive · ${driveDoneCount}/${driveTotalCount} · ${driveFailedCount} failed`
                        : driveActiveCount > 0
                          ? `Drive · ${driveDoneCount}/${driveTotalCount} · ${driveActiveCount} active`
                          : `Drive · ${driveDoneCount}/${driveTotalCount}`
                    }
                  </span>
                  {/* Inline retry link — only shown when there are Drive failures */}
                  {driveFailedCount > 0 && (
                    <>
                      <span className="opacity-40">·</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); retryAllFailed(); }}
                        disabled={isRetryingAll}
                        className={cn(
                          "underline underline-offset-2 font-semibold transition-opacity",
                          isRetryingAll ? "opacity-50 cursor-not-allowed" : "hover:opacity-80 cursor-pointer"
                        )}
                        title={`Retry ${driveFailedCount} failed Drive upload${driveFailedCount !== 1 ? "s" : ""}`}
                      >
                        {isRetryingAll ? "Retrying…" : "Retry"}
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* Drive queued pill — Drive is a destination but phase hasn't started yet */}
              {driveIsDestination && !drivePhaseStarted && doneCount < totalQueued && (
                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-muted/60 border-border text-muted-foreground">
                  <Cloud className="w-3 h-3" />
                  <span>Drive · queued</span>
                </div>
              )}
            </div>
          )}
          {/* Destination icons — always visible */}
          {wizardState.destinations.includes("local") && (
            <div className="w-7 h-7 rounded-lg bg-background border border-border flex items-center justify-center" title="Local Drive">
              <Download className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
          )}
          {wizardState.destinations.includes("google_drive") && (
            <div className="w-7 h-7 rounded-lg bg-background border border-border flex items-center justify-center" title="Google Drive">
              <Cloud className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
      {/* Overall upload progress bar — shown below the session banner during upload */}
      {appPhase === "uploading" && totalQueued > 0 && doneCount < totalQueued && (
        <div className="space-y-1">
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span className="font-medium">
              {drivePhaseStarted && driveActiveCount > 0
                ? `Drive: transferring ${driveActiveCount} file${driveActiveCount !== 1 ? "s" : ""}…`
                : drivePhaseStarted
                  ? `Drive: ${driveDoneCount}/${driveTotalCount} transferred…`
                  : activelyUploading > 0
                    ? `Uploading ${activelyUploading} file${activelyUploading !== 1 ? "s" : ""} simultaneously…`
                    : "Preparing uploads…"
              }
            </span>
            <span className="tabular-nums">{overallUploadPct}%</span>
          </div>
          <Progress value={overallUploadPct} className="h-1.5" />
        </div>
      )}

      {/* ── PHASE: Adding files ── */}
      {(appPhase === "adding") && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary" />
              {isProvider ? "Step 2: Add Files & Tag Topics" : "Step 2: Add Files & Tag Activities"}
            </CardTitle>
            <CardDescription>
              Drop your footage files here, add {isProvider ? "topic" : "activity"} tags to each clip, then click <strong>Upload &amp; Rename All</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 cursor-pointer",
                isDragOver
                  ? "border-primary bg-primary/5 scale-[1.01]"
                  : "border-border hover:border-primary/50 hover:bg-muted/30"
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={`video/*,image/*,.mov,.mp4,.avi,.mkv,.wmv,.flv,.webm,.heic,.heif,${AUDIO_ACCEPT_FRAGMENT}`}
                onChange={handleFileInput}
                className="hidden"
              />
              <div className="flex flex-col items-center gap-3">
                <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center transition-all", isDragOver ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                  <Upload className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground">{isDragOver ? "Drop files here" : "Drag & drop footage files"}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    or <span className="text-primary font-medium">click to browse</span> — MP4, MOV, AVI, JPG, HEIC, and more
                  </p>
                </div>
                {files.length > 0 && <Badge variant="secondary">{files.length} file{files.length !== 1 ? "s" : ""} added</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}



      {/* ── Batch Size Warning ── */}
      {(() => {
        const GB = 1024 ** 3;
        const FILE_COUNT_LIMIT = 30;
        // Bedrock transcription caps at 4 GB per file. Applies to provider
        // A-Roll always, and to patient clips that are tagged for transcription
        // (see TRANSCRIBE_TRIGGER_ACTIVITY_TAGS in src/shared/const.ts).
        const PER_FILE_LIMIT_BYTES = 3.5 * GB;
        const TOTAL_BATCH_LIMIT_BYTES = 20 * GB;

        const totalBytes = files.reduce((sum, f) => sum + (f.file?.size ?? 0), 0);
        const oversizedFiles = files.filter((f) => {
          if ((f.file?.size ?? 0) <= PER_FILE_LIMIT_BYTES) return false;
          return isProvider || activitiesTriggerTranscription(f.activities);
        });

        const overCount = files.length > FILE_COUNT_LIMIT;
        const overPerFile = oversizedFiles.length > 0;
        const overTotal = totalBytes > TOTAL_BATCH_LIMIT_BYTES;
        if (!overCount && !overPerFile && !overTotal) return null;

        const reasons: string[] = [];
        if (overCount) reasons.push(`${files.length} files queued (recommended max: ${FILE_COUNT_LIMIT})`);
        if (overPerFile) reasons.push(`${oversizedFiles.length} file${oversizedFiles.length === 1 ? "" : "s"} over 3.5 GB (transcription caps at 4 GB per file)`);
        if (overTotal) reasons.push(`${(totalBytes / GB).toFixed(1)} GB total (long sessions are more prone to interruption)`);

        return (
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800">
            <TriangleAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
            <div className="text-sm">
              <span className="font-semibold">Large batch detected — </span>
              {reasons.join(" · ")}. The upload will still work, but smaller batches finish faster and recover better from interruptions.
            </div>
          </div>
        );
      })()}

      {/* ── File List ── */}
      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((entry, index) => {
            const isEditable = appPhase === "adding" && (entry.phase === "tagging" || entry.phase === "error");
            const isActive =
              entry.phase === "uploading" ||
              entry.phase === "uploading_final" ||
              entry.phase === "transcribing";

            return (
              <Card
                key={index}
                className={cn(
                  "transition-all",
                  entry.phase === "done" && "border-emerald-200 bg-emerald-50/30",
                  entry.phase === "error" && "border-red-200 bg-red-50/30",
                  entry.phase === "tagging" && entry.activities.length > 0 && "border-amber-300 bg-amber-50/20",
                  // Intentionally skipped → orange, so a quick scroll shows it was
                  // handled (vs. yellow = tagged, plain = not yet touched).
                  entry.phase === "tagging" && entry.activities.length === 0 && entry.tagSkipped && "border-orange-300 bg-orange-50/40",
                  entry.phase === "tagging" && entry.activities.length === 0 && !entry.tagSkipped && "border-border bg-background",
                  entry.phase === "uploading" && "border-violet-200 bg-violet-50/20",
                  entry.phase === "skipped" && "border-orange-200 bg-orange-50/20",
                  entry.phase === "queued" && "border-border",
                )}
              >
                <CardContent className="pt-4 pb-4">
                  {/* Header row */}
                  <div className="flex items-start gap-3">
                    <VideoThumbnail
                      file={entry.file}
                      thumbnail={entry.thumbnail}
                      thumbnailWidth={entry.thumbnailWidth}
                      thumbnailHeight={entry.thumbnailHeight}
                      className="mt-0.5"
                      isImage={isImageFile(entry.file)}
                      isAudio={isAudioFile(entry.file)}
                    />

                    <div className="flex-1 min-w-0">
                      {/* Original name + status badge */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-foreground truncate max-w-[220px]">
                          {entry.originalName}
                        </span>
                        <Badge className={cn("text-xs px-2 py-0 border-0", PHASE_COLORS[entry.phase])}>
                          {isActive && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                          {PHASE_LABELS[entry.phase]}
                        </Badge>
                        {/* AI sidecar status badge — appears after upload
                            completes while server-side Pegasus + Whisper +
                            embedding work is still in flight. Resolves to a
                            green check ("AI saved") when the row's
                            ai_description + embedding are persisted, or a
                            red warning if the analysis failed. */}
                        {entry.phase === "done" && entry.pegasusPhase && (
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border",
                              entry.pegasusPhase === "pending" &&
                                "bg-violet-50 text-violet-700 border-violet-200",
                              entry.pegasusPhase === "done" &&
                                "bg-emerald-50 text-emerald-700 border-emerald-200",
                              entry.pegasusPhase === "failed" &&
                                "bg-red-50 text-red-600 border-red-200",
                            )}
                            title={
                              entry.pegasusPhase === "pending"
                                ? "Generating AI description, transcript, and search embedding — large sources may take a few minutes."
                                : entry.pegasusPhase === "done"
                                  ? "AI description, transcript, and embedding saved to the search table."
                                  : "AI analysis failed — clip is uploaded but won't appear in semantic search results."
                            }
                          >
                            {entry.pegasusPhase === "pending" && (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            )}
                            {entry.pegasusPhase === "done" && (
                              <CheckCircle2 className="w-3 h-3" />
                            )}
                            {entry.pegasusPhase === "failed" && (
                              <AlertCircle className="w-3 h-3" />
                            )}
                            <Sparkles className="w-3 h-3" />
                            {entry.pegasusPhase === "pending" && "Analyzing…"}
                            {entry.pegasusPhase === "done" && "AI saved"}
                            {entry.pegasusPhase === "failed" && "AI failed"}
                          </span>
                        )}
                        {/* Google Drive upload status badge */}
                        {wizardState.destinations.includes("google_drive") && entry.drivePhase && (
                          entry.drivePhase === "error" ? (
                            <button
                              onClick={() => retryDriveUpload(index)}
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border bg-red-50 text-red-600 border-red-200 hover:bg-red-100 transition-colors"
                              title="Google Drive transfer failed — click to retry"
                            >
                              <AlertCircle className="w-3 h-3" />
                              <Cloud className="w-3 h-3" />
                              Drive failed
                              <RefreshCw className="w-3 h-3 ml-0.5" />
                            </button>
                          ) : (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border",
                                entry.drivePhase === "uploading" && "bg-[#4986e7]/10 text-[#4986e7] border-[#4986e7]/30",
                                entry.drivePhase === "done" && "bg-emerald-50 text-emerald-700 border-emerald-200",
                              )}
                              title={entry.drivePhase === "uploading" ? "Uploading to Google Drive..." : "Saved to Google Drive"}
                            >
                              {entry.drivePhase === "uploading" && <Loader2 className="w-3 h-3 animate-spin" />}
                              {entry.drivePhase === "done" && <CheckCircle2 className="w-3 h-3" />}
                              <Cloud className="w-3 h-3" />
                              {entry.drivePhase === "uploading" && "Drive..."}
                              {entry.drivePhase === "done" && "Drive"}
                            </span>
                          )
                        )}
                      </div>

                      {/* Renamed preview */}
                      {(entry.phase === "tagging" || entry.phase === "done" || entry.phase === "uploading_final") && entry.activities.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">→</span>
                          <span className="text-xs font-medium text-foreground font-mono bg-muted/60 px-2 py-0.5 rounded">
                            {entry.renamedName}
                          </span>
                        </div>
                      )}

                      {/* Upload Progress + ETA */}
                      {isActive && (
                        <div className="mt-2">
                          <Progress value={entry.progress} className="h-1" />
                          {(entry.phase === "uploading" || entry.phase === "uploading_final") && (
                            <div className="flex items-center gap-2 mt-1">
                              {entry.speedBps !== undefined && entry.speedBps > 0 ? (
                                <>
                                  <span className="text-xs font-medium text-violet-600">
                                    {formatSpeed(entry.speedBps)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {entry.etaSec !== undefined ? formatEta(entry.etaSec) : ""}
                                  </span>
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground">Uploading...</span>
                              )}
                            </div>
                          )}
                          {entry.phase === "transcribing" && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-medium text-violet-600">
                                {entry.transcribePhase === "extracting"
                                  ? `Extracting audio… ${entry.progress}%`
                                  : entry.transcribePhase === "uploading"
                                    ? `Uploading audio… ${entry.progress}%`
                                    : "Transcribing & summarizing…"}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Drive Progress + ETA */}
                      {entry.drivePhase === "uploading" && (
                        <div className="mt-2">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Cloud className="w-3 h-3 text-[#4986e7]" />
                            <span className="text-xs text-[#4986e7] font-medium">Drive</span>
                          </div>
                          <Progress
                            value={entry.driveProgress ?? 0}
                            className="h-1 [&>div]:bg-[#4986e7]"
                          />
                          <div className="flex items-center gap-2 mt-1">
                            {entry.driveSpeedBps !== undefined && entry.driveSpeedBps > 0 ? (
                              <>
                                <span className="text-xs font-medium text-[#4986e7]">
                                  {formatSpeed(entry.driveSpeedBps)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {entry.driveEtaSec !== undefined ? formatEta(entry.driveEtaSec) : ""}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Uploading to Drive...</span>
                            )}
                          </div>
                        </div>
                      )}

                      {entry.error && (
                        <p className="text-xs text-red-600 mt-1">{entry.error}</p>
                      )}
                    </div>

                    {/* Right-side actions */}
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {entry.phase === "done" && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                      {entry.phase === "error" && <AlertCircle className="w-5 h-5 text-red-500" />}
                      {entry.phase === "skipped" && (
                        <span className="text-xs text-orange-500 font-medium">Skipped</span>
                      )}
                      {/* Skip button — visible while the file is actively uploading to cloud storage */}
                      {(entry.phase === "uploading" || entry.phase === "uploading_final") && (
                        <button
                          onClick={() => setSkipConfirmIndex(index)}
                          className="ml-1 text-xs text-orange-500 hover:text-orange-700 border border-orange-300 hover:border-orange-500 rounded px-2 py-0.5 transition-colors bg-orange-50 hover:bg-orange-100 whitespace-nowrap"
                          title="Skip cloud upload for this file"
                        >
                          Skip Upload
                        </button>
                      )}
                      {(entry.phase === "tagging" || entry.phase === "queued") && appPhase === "adding" && (
                        <button onClick={() => removeFile(index)} className="text-muted-foreground hover:text-foreground">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Activity tags — shown on tagging cards */}
                  {entry.phase === "tagging" && appPhase === "adding" && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {isProvider ? "Topic Tags" : "Activity Tags"}
                        </span>
                      </div>
                      <LabelEditor
                        activities={entry.activities}
                        onChange={(updated) => updateActivities(index, updated)}
                        disabled={false}
                        sessionSuggestions={Array.from(
                          new Set(
                            files
                              .filter((_, i) => i !== index)
                              .flatMap((f) => f.activities)
                          )
                        )}
                        subjectKind={subjectKind}
                      />
                      {/* Skip Tag — only meaningful while the clip has no tags.
                          Marks it as intentionally blank so it won't be flagged
                          in the untagged-clips warning at finalize. */}
                      {entry.activities.length === 0 && (
                        entry.tagSkipped ? (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-orange-700 italic">
                              Tag intentionally skipped — will save as{" "}
                              <span className="font-mono not-italic">{buildRenamedName(entry.originalName, [])}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleSkipTag(index)}
                              className="text-xs text-primary hover:underline font-medium shrink-0"
                            >
                              Undo
                            </button>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => toggleSkipTag(index)}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border border-border bg-muted/60 text-foreground/80 hover:bg-muted transition-colors"
                            >
                              Skip tag for this clip
                            </button>
                          </div>
                        )
                      )}
                      {/* Per-clip provider add-on — patient flow only, subtle
                          and collapsed by default. Additive to the batch. */}
                      {!isProvider && (
                        <ClipProviderAddon
                          value={entry.providers ?? []}
                          onChange={(next) => updateProviders(index, next)}
                          sessionSuggestions={clipProviderCounts.map((c) => c.name)}
                        />
                      )}
                      {/* The renamed-name preview already appears at the top of
                       * the card (next to the original filename, with a → arrow)
                       * during the tagging phase, so a second "Will be saved as:"
                       * below the label editor is redundant. The error-phase
                       * block below keeps its preview because the top arrow is
                       * suppressed when phase === "error". */}
                    </div>
                  )}

                  {/* Error details — shown on error-phase cards */}
                  {entry.phase === "error" && (
                    <div className="mt-3 pt-3 border-t border-red-200/50">
                      {entry.errorReason && (
                        <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-xs text-red-800 leading-relaxed">{entry.errorReason}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mb-1">
                        <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {isProvider ? "Topic Tags" : "Activity Tags"}
                        </span>
                      </div>
                      <LabelEditor
                        activities={entry.activities}
                        onChange={(updated) => updateActivities(index, updated)}
                        disabled={false}
                        sessionSuggestions={Array.from(
                          new Set(
                            files
                              .filter((_, i) => i !== index)
                              .flatMap((f) => f.activities)
                          )
                        )}
                        subjectKind={subjectKind}
                      />
                      {entry.activities.length > 0 && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">Will be saved as:</span>
                          <span className="text-xs font-medium text-primary font-mono bg-primary/8 dark:bg-primary/25 px-2 py-0.5 rounded">
                            {buildRenamedName(entry.originalName, entry.activities)}
                          </span>
                        </div>
                      )}
                      {/* Per-card Retry Upload button */}
                      <div className="mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={retryingIndices.has(index)}
                          className="w-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 flex items-center gap-2"
                          onClick={async () => {
                            setRetryingIndices((prev) => new Set(Array.from(prev).concat(index)));
                            updateFile(index, { phase: "tagging", error: undefined, errorReason: undefined, progress: 0 });
                            await new Promise(r => setTimeout(r, 80));
                            try {
                              await finalizeAll();
                            } finally {
                              setRetryingIndices((prev) => { const s = new Set(prev); s.delete(index); return s; });
                            }
                          }}
                        >
                          {retryingIndices.has(index) ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Retrying...</>
                          ) : (
                            <><RefreshCw className="w-3.5 h-3.5" /> Retry Upload</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Label editor — shown in done phase (read-only) */}
                  {entry.phase === "done" && entry.activities.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">Saved as:</span>
                        <span className="text-xs font-medium text-emerald-700 font-mono bg-emerald-50 px-2 py-0.5 rounded break-all">
                          {entry.renamedName}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Storage health badge removed — uploads now go straight to Drive,
          there's no intermediary service whose health to surface. */}

      {/* Google Drive note */}
      {wizardState.destinations.includes("google_drive") && appPhase === "adding" && files.length > 0 && (
        <div className="p-4 bg-green-50 rounded-xl border border-green-200">
          <div className="flex items-start gap-3">
            <Cloud className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-800">Google Drive Upload</p>
              <p className="text-xs text-green-700 mt-1">
                Files will be saved with the renamed labels. To sync to Google Drive, use the Google Drive desktop app and place files in{" "}
                <strong>{wizardState.patientName}/{sessionFolderBreadcrumb}/</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Buttons ── */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={appPhase === "uploading"}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        {/* CTA: Upload & Rename All */}
        {appPhase === "adding" && (
          <Button
            onClick={handleFinalizeClick}
            disabled={taggingCount === 0}
            className="flex-1 h-12 text-base font-semibold flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Upload &amp; Rename All ({taggingCount} file{taggingCount !== 1 ? "s" : ""})
          </Button>
        )}

        {/* Uploading state */}
        {appPhase === "uploading" && (
          <Button disabled className="flex-1 h-12 text-base font-semibold flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading {doneCount}/{files.length} files...
          </Button>
        )}
      </div>

      {/* No-tag confirmation dialog */}
      <AlertDialog open={noTagDialog.open} onOpenChange={(open) => setNoTagDialog((d) => ({ ...d, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upload clips without {isProvider ? "topic" : "activity"} tags?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const noTagCount = files.filter(
                  (f) => (f.phase === "tagging" || f.phase === "error") && !isImageFile(f.file) && f.activities.length === 0 && !f.tagSkipped
                ).length;
                return (
                  <>
                    {noTagCount} clip{noTagCount !== 1 ? "s have" : " has"} no {isProvider ? "topic" : "activity"} tags and will be uploaded using the original filename. You can add tags now or proceed without them.
                  </>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setNoTagDialog((d) => ({ ...d, open: false }))}>Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={noTagDialog.onConfirm}>Upload Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New-tag confirmation dialog */}
      <Dialog
        open={newTagDialog.open}
        onOpenChange={(open) => {
          if (newTagDialog.submitting) return; // ignore close attempts mid-submit
          if (!open) setNewTagDialog({ open: false, entries: [], submitting: false });
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add {newTagDialog.entries.length === 1 ? "a new" : `${newTagDialog.entries.length} new`} {isProvider ? "Topic" : "Activity"} {newTagDialog.entries.length === 1 ? "Tag" : "Tags"} to the Library?
            </DialogTitle>
            <DialogDescription>
              {newTagDialog.entries.length === 1 ? "This tag isn't" : "These tags aren't"} in the {isProvider ? "Topic" : "Activity"} Library yet. Confirm to add {newTagDialog.entries.length === 1 ? "it" : "them"} — descriptions are optional and help others understand what the tag means.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {newTagDialog.entries.map((entry, idx) => (
              <div key={`${entry.originalName}-${idx}`} className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Tag name</label>
                  <Input
                    value={entry.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewTagDialog((d) => ({
                        ...d,
                        entries: d.entries.map((x, i) => (i === idx ? { ...x, name: value } : x)),
                      }));
                    }}
                    disabled={newTagDialog.submitting}
                    maxLength={255}
                    className="h-9 text-sm font-semibold"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Description <span className="text-muted-foreground/70">(optional)</span>
                  </label>
                  <textarea
                    value={entry.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewTagDialog((d) => ({
                        ...d,
                        entries: d.entries.map((x, i) => (i === idx ? { ...x, description: value } : x)),
                      }));
                    }}
                    disabled={newTagDialog.submitting}
                    placeholder="What does this tag mean? (e.g. 'Cold laser therapy session')"
                    maxLength={500}
                    rows={2}
                    className="w-full text-xs px-2.5 py-1.5 rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  />
                </div>
                {entry.fileIndices.length > 1 && (
                  <p className="text-[11px] text-muted-foreground">
                    Used on {entry.fileIndices.length} files in this batch.
                  </p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewTagDialog({ open: false, entries: [], submitting: false })}
              disabled={newTagDialog.submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmNewTags} disabled={newTagDialog.submitting}>
              {newTagDialog.submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Adding…
                </>
              ) : (
                `Add ${newTagDialog.entries.length === 1 ? "Tag" : "Tags"} & Upload`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ── Unified failure banner — shown when any S3 or Drive uploads have failed ── */}
      {(appPhase === "adding" && files.some((f) => f.phase === "error")) || files.some((f) => f.drivePhase === "error") ? (() => {
        const s3Failed = files.filter((f) => f.phase === "error");
        const driveFailed = files.filter((f) => f.drivePhase === "error");
        const totalFailed = s3Failed.length + driveFailed.length;
        const hasS3 = s3Failed.length > 0;
        const hasDrive = driveFailed.length > 0;
        const bothFailed = hasS3 && hasDrive;
        const title = bothFailed
          ? `${totalFailed} upload failure${totalFailed !== 1 ? "s" : ""} — cloud storage & Google Drive`
          : hasS3
          ? `${s3Failed.length} file${s3Failed.length !== 1 ? "s" : ""} failed to upload to cloud storage`
          : `${driveFailed.length} Google Drive upload${driveFailed.length !== 1 ? "s" : ""} failed`;
        const subtitle = bothFailed
          ? `${s3Failed.length} file${s3Failed.length !== 1 ? "s" : ""} failed cloud storage and ${driveFailed.length} failed Google Drive. Files saved locally. Retry before continuing.`
          : hasS3
          ? `${s3Failed.length} file${s3Failed.length !== 1 ? "s" : ""} could not be saved to cloud storage. These files were saved locally.`
          : `${driveFailed.length} file${driveFailed.length !== 1 ? "s" : ""} could not be saved to Google Drive. Please retry before continuing.`;
        return (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">{title}</p>
                <p className="text-xs text-red-700 mt-0.5">{subtitle}</p>
              </div>
            </div>

            {/* Per-section file lists when both types failed */}
            {bothFailed && (
              <div className="space-y-2 pl-8">
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">Cloud storage failures ({s3Failed.length})</p>
                  <ul className="text-xs space-y-0.5">
                    {s3Failed.map((f) => (
                      <li key={`s3-${f.originalName}`} className="flex items-center gap-1.5 text-red-800">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate font-medium">{f.renamedName || f.originalName}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">Google Drive failures ({driveFailed.length})</p>
                  <ul className="text-xs space-y-0.5">
                    {driveFailed.map((f) => (
                      <li key={`drive-${f.originalName}`} className="flex items-center gap-1.5 text-red-800">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate font-medium">{f.renamedName || f.originalName}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-center gap-2">
              {/* Retry button — retries S3 failures by re-running the upload, Drive failures via retryAllFailed */}
              {hasS3 && (
                <Button
                  variant="outline"
                  onClick={handleFinalizeClick}
                  disabled={isRetryingAll}
                  className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700 bg-background dark:bg-card dark:hover:bg-red-950/30"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry Cloud Storage ({s3Failed.length})
                </Button>
              )}
              {hasDrive && (
                <Button
                  variant="outline"
                  onClick={retryAllFailed}
                  disabled={isRetryingAll}
                  className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700 bg-background dark:bg-card dark:hover:bg-red-950/30"
                >
                  {isRetryingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {isRetryingAll ? "Retrying Drive…" : `Retry Google Drive (${driveFailed.length})`}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setProceedAnywayOpen(true)}
                disabled={isRetryingAll}
                className="flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800 bg-background dark:bg-card dark:hover:bg-orange-950/30"
              >
                Proceed Anyway
              </Button>
            </div>
          </div>
        );
      })() : null}

      {/* Skip confirmation dialog — shown when user clicks Skip Upload on a card */}
      <AlertDialog open={skipConfirmIndex !== null} onOpenChange={(open) => { if (!open) setSkipConfirmIndex(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Skip cloud upload?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Are you sure you want to skip the cloud upload for{" "}
                  <strong className="text-foreground">
                    {skipConfirmIndex !== null ? (files[skipConfirmIndex]?.renamedName || files[skipConfirmIndex]?.originalName) : "this file"}
                  </strong>?
                </p>
                <p className="text-orange-700 font-medium">
                  The file will <strong>not</strong> be uploaded to cloud storage. It will appear as
                  "Skipped" on the summary screen.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSkipConfirmIndex(null)}>Cancel — Keep Uploading</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => { if (skipConfirmIndex !== null) skipFile(skipConfirmIndex); }}
            >
              Yes, Skip This File
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unified proceed-anyway confirmation dialog */}
      <AlertDialog open={proceedAnywayOpen} onOpenChange={setProceedAnywayOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Proceed with incomplete uploads?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {(() => {
                  const s3Failed = files.filter((f) => f.phase === "error");
                  const driveFailed = files.filter((f) => f.drivePhase === "error");
                  const skipped = files.filter((f) => f.phase === "skipped");
                  const allFailed = [
                    ...s3Failed.map((f) => ({ file: f, type: "Cloud Storage" as const, isSkipped: false })),
                    ...driveFailed.map((f) => ({ file: f, type: "Google Drive" as const, isSkipped: false })),
                  ];
                  const totalIssues = allFailed.length + skipped.length;
                  return (
                    <>
                      {allFailed.length > 0 && (
                        <>
                          <p>The following {allFailed.length === 1 ? "file" : `${allFailed.length} files`} did <strong>not</strong> upload successfully:</p>
                          <ul className="text-sm bg-red-50 border border-red-200 rounded-md p-3 space-y-1 max-h-36 overflow-y-auto">
                            {allFailed.map(({ file: f, type }) => (
                              <li key={`${type}-${f.originalName}`} className="flex items-center gap-2">
                                <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                                <span className="font-medium truncate text-red-800">{f.renamedName || f.originalName}</span>
                                <span className="text-xs text-red-500 whitespace-nowrap">{type}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {skipped.length > 0 && (
                        <>
                          <p>{skipped.length === 1 ? "1 file was" : `${skipped.length} files were`} <strong>skipped</strong>:</p>
                          <ul className="text-sm bg-orange-50 border border-orange-200 rounded-md p-3 space-y-1 max-h-36 overflow-y-auto">
                            {skipped.map((f) => (
                              <li key={f.originalName} className="flex items-center gap-2">
                                <TriangleAlert className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                                <span className="font-medium truncate text-orange-800">{f.renamedName || f.originalName}</span>
                                <span className="text-xs text-orange-500 whitespace-nowrap">Skipped</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      <p className="text-orange-700 font-medium text-sm">
                        {s3Failed.length > 0
                          ? "Failed cloud storage files were saved locally. They will need to be uploaded manually."
                          : driveFailed.length > 0
                          ? "Failed Drive files will need to be uploaded to Google Drive manually."
                          : skipped.length > 0
                          ? "Skipped files were not uploaded to cloud storage."
                          : ""}
                      </p>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={isRetryingAll} className="sm:mr-auto">Go Back &amp; Retry</AlertDialogCancel>
            {/* Retry button — only shown when there are Drive failures (S3 failures can't be retried from here) */}
            {files.some((f) => f.drivePhase === "error") && (
              <Button
                variant="outline"
                className="flex items-center gap-2 border-primary/40 text-primary hover:bg-primary/5"
                disabled={isRetryingAll}
                onClick={() => {
                  // Run retryAllFailed without closing the dialog.
                  // On full success retryAllFailed will close the dialog and navigate automatically.
                  retryAllFailed();
                }}
              >
                {isRetryingAll ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Retrying…</>
                ) : (
                  <><RefreshCw className="w-4 h-4" /> Retry Failed Uploads</>
                )}
              </Button>
            )}
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={isRetryingAll}
              onClick={() => {
                setProceedAnywayOpen(false);
                setS3ProceedAnywayOpen(false);
                if (processedFilesRef.current.length > 0) {
                  setTimeout(() => onComplete(processedFilesRef.current, resolvedTopicRef.current), 300);
                }
              }}
            >
              I Understand — Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Provider A-Roll: post-upload per-file Topic Tag review dialog. */}
      <Dialog
        open={tagReviewDialog.open}
        onOpenChange={(open) => {
          if (open || applyingTagSuggestions || tagReviewDialog.fetching) return;
          // Closing while suggestions are visible = "skip all".
          tagReviewDialog.resolve?.(null);
          setTagReviewDialog({ open: false, rows: [], resolve: null, fetching: false });
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Review AI-Suggested Topic Tags
            </DialogTitle>
            <DialogDescription>
              {tagReviewDialog.fetching ? (
                <>Asking Claude for a Topic Tag for each untagged clip…</>
              ) : (
                <>
                  These clips were uploaded without a Topic Tag. Edit the
                  suggestion or check Skip to leave a file untagged.
                  Accepting renames the file on Google Drive, in your local
                  folder, and updates the database row.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {tagReviewDialog.fetching ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6 space-y-3 py-2">
              {tagReviewDialog.rows.map((row, idx) => (
                <div
                  key={`${row.dbFileId}-${idx}`}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-xl border transition-all",
                    row.skipped
                      ? "bg-muted/30 border-border opacity-60"
                      : "bg-background border-border",
                  )}
                >
                  <VideoThumbnail thumbnail={row.thumbnail} />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="text-xs text-muted-foreground truncate">
                      {row.originalName}
                    </div>
                    <Input
                      type="text"
                      value={row.edited}
                      disabled={row.skipped}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setTagReviewDialog((d) => ({
                          ...d,
                          rows: d.rows.map((r, i) =>
                            i === idx ? { ...r, edited: newVal } : r,
                          ),
                        }));
                      }}
                      className="h-9 text-sm"
                      placeholder="Topic Tag"
                      autoComplete="off"
                    />
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      → {row.edited.trim()
                          ? buildRenamedName(row.originalName, [row.edited.trim()])
                          : row.originalName}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTagReviewDialog((d) => ({
                        ...d,
                        rows: d.rows.map((r, i) =>
                          i === idx ? { ...r, skipped: !r.skipped } : r,
                        ),
                      }));
                    }}
                    className={cn(
                      "text-xs font-medium px-2 py-1 rounded-md border transition-colors flex-shrink-0",
                      row.skipped
                        ? "border-primary text-primary hover:bg-primary/10"
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {row.skipped ? "Include" : "Skip"}
                  </button>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={applyingTagSuggestions || tagReviewDialog.fetching}
              onClick={() => {
                tagReviewDialog.resolve?.(null);
                setTagReviewDialog({ open: false, rows: [], resolve: null, fetching: false });
              }}
            >
              Skip All
            </Button>
            <Button
              type="button"
              disabled={
                applyingTagSuggestions ||
                tagReviewDialog.fetching ||
                tagReviewDialog.rows.every((r) => r.skipped || !r.edited.trim())
              }
              className="gap-1.5"
              onClick={() => {
                const resolved = tagReviewDialog.rows;
                tagReviewDialog.resolve?.(resolved);
                setTagReviewDialog({ open: false, rows: [], resolve: null, fetching: false });
              }}
            >
              {applyingTagSuggestions ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>
              ) : (
                <><Check className="w-4 h-4" /> Apply Tags</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
